import React, { useState, useEffect } from "react";
import "./App.css";
import "./mobile.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';

// 2FA Components
import TwoFactorManagement from "./components/TwoFactorManagement";
import ChatPopup from "./components/ChatPopup";
import ChatNotifications from "./components/ChatNotifications";
import AdminBackupExport from "./components/AdminBackupExport";
import FinancialManagement from "./components/FinancialManagement";
import StatusManager from "./components/StatusManager";
import SMSConfiguration from "./components/SMSConfiguration";
import MessagingConfiguration from "./components/MessagingConfiguration";
import MessagingButtons from "./components/MessagingButtons";
import PublicStatusCheck from "./components/PublicStatusCheck";
import OrdersManager from "./components/OrdersManager";
import InstallationManager from "./components/InstallationManager";

// Shadcn UI Components
import { Button } from "./components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Badge } from "./components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Checkbox } from "./components/ui/checkbox";
import { toast } from "sonner";
import { Toaster } from "./components/ui/sonner";

// Icons
import { 
  Monitor, Phone, Mail, Calendar, Settings, 
  Plus, Search, Filter, CheckCircle, Clock, 
  Wrench, TestTube, User, MapPin, Hash, Printer,
  Smartphone, Eye, Euro, X, Edit3, FileText, Save,
  Activity, TrendingUp, Users, Calendar as CalendarIcon,
  Camera, Upload, UserPlus, Shield, Trash2, MessageCircle, Send,
  Download, Bell, Receipt, ShoppingCart
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_techfix-manager-4/artifacts/4023hqjk_Logo%20IT.jpg";

// Default status mapping (will be replaced by dynamic data)
const defaultStatusMap = {
  ne_process: { label: "Në Process", color: "bg-blue-500", icon: Clock },
  ne_riparim: { label: "Në Riparim", color: "bg-orange-500", icon: Wrench },
  ne_testim: { label: "Në Testim", color: "bg-purple-500", icon: TestTube },
  gati: { label: "Gati", color: "bg-green-500", icon: CheckCircle }
};

// Navigation Component
const Navigation = ({ user, onLogout }) => {
  const location = useLocation();
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);

  // Fetch pending orders count
  useEffect(() => {
    const fetchPendingOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        const pending = response.data.filter(o => o.status === 'pending').length;
        setPendingOrdersCount(pending);
      } catch (error) {
        console.error('Error fetching orders:', error);
      }
    };

    fetchPendingOrders();
    // Poll every 30 seconds
    const interval = setInterval(fetchPendingOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAdminDropdown && !event.target.closest('.admin-dropdown')) {
        setShowAdminDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showAdminDropdown]);

  const navItems = [
    { path: "/", label: "Dashboard", icon: Monitor },
    { path: "/new-ticket", label: "Tiket e Re", icon: Plus },
    { path: "/tickets", label: "Të Gjitha Tiketat", icon: Settings },
    { path: "/clients", label: "Klientët", icon: User },
    { path: "/orders", label: "Porosi", icon: ShoppingCart },
  ];

  // Only show admin items if user is admin
  const adminItems = user?.role === 'admin' ? [
    { path: "/admin", label: "Konfigurimi", icon: Edit3 },
    { path: "/admin/backup", label: "Backup i DB", icon: Download },
    { path: "/admin/clients-export", label: "Export Klientët", icon: FileText },
    { path: "/admin/finances", label: "Financat", icon: Euro },
    { path: "/admin/status-manager", label: "Statuset", icon: Settings },
    { path: "/admin/sms-config", label: "SMS Konfigurimi", icon: MessageCircle },
    { path: "/admin/messaging-config", label: "WhatsApp & Viber", icon: MessageCircle },
    { path: "/users", label: "Përdoruesit", icon: UserPlus },
    { path: "/2fa-settings", label: "2FA Siguria", icon: Shield },
  ] : [];

  const isAdminActive = adminItems.some(item => location.pathname === item.path);

  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <img src={LOGO_URL} alt="IT Solution" className="h-12 w-auto" />
          </div>
          <div className="flex space-x-8 relative">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const showBadge = item.path === '/orders' && pendingOrdersCount > 0;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors relative ${
                    isActive
                      ? "border-slate-800 text-slate-900"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {item.label}
                  {showBadge && (
                    <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-600 rounded-full animate-pulse">
                      {pendingOrdersCount}
                    </span>
                  )}
                </Link>
              );
            })}
            
            {/* Admin Dropdown - Only show for admin users */}
            {user?.role === 'admin' && (
              <div className="relative admin-dropdown">
                <button
                  onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium border-b-2 transition-colors h-16 ${
                    isAdminActive
                      ? "border-slate-800 text-slate-900"
                      : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
                  }`}
                  style={{ background: 'none', outline: 'none' }}
                >
                  <Edit3 className="w-4 h-4 mr-2" />
                  Admin
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showAdminDropdown && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-[60] border">
                    {adminItems.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setShowAdminDropdown(false)}
                          className={`flex items-center px-4 py-2 text-sm transition-colors duration-200 hover:bg-slate-100 ${
                            isActive
                              ? "bg-blue-50 text-blue-600"
                              : "text-slate-700"
                          }`}
                        >
                          <Icon className="w-4 h-4 mr-3" />
                          {item.label}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {/* User Info & Logout */}
            {user && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-bold">
                      {user.emri ? user.emri.charAt(0).toUpperCase() : user.username.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-slate-900">{user.emri || user.username}</p>
                    <p className="text-slate-500 capitalize">{user.role}</p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onLogout}
                  className="text-slate-600 hover:text-slate-900"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  Dil
                </Button>
              </div>
            )}
            
            <Link
              to="/status"
              className="inline-flex items-center px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 border border-slate-300 rounded-md hover:bg-slate-50"
            >
              <Smartphone className="w-4 h-4 mr-2" />
              App Mobile
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

// Client Report Component
const ClientReport = () => {
  const { clientId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: 'all',
    dateFrom: '',
    dateTo: '',
    ticketId: ''
  });

  useEffect(() => {
    fetchClientReport();
  }, [clientId]);

  const fetchClientReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status_filter', filters.status);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.ticketId) params.append('ticket_id_filter', filters.ticketId);

      const response = await axios.get(`${API}/clients/${clientId}/report?${params}`);
      setReport(response.data);
    } catch (error) {
      toast.error("Gabim në ngarkimin e raportit");
      console.error("Fetch client report error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchClientReport();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Duke ngarkuar raportin...</div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-500">Nuk u gjet raporti për klientin</p>
        <Link to="/clients" className="text-blue-600 hover:text-blue-800">
          Kthehu te lista e klientëve
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Client Info Header */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="flex items-center text-2xl">
                <User className="w-6 h-6 mr-3" />
                {report.client.emri}
              </CardTitle>
              <CardDescription className="mt-2">
                <div className="flex items-center space-x-4 text-sm">
                  <span className="flex items-center">
                    <Phone className="w-4 h-4 mr-1" />
                    {report.client.telefoni}
                  </span>
                  {report.client.email && (
                    <span className="flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      {report.client.email}
                    </span>
                  )}
                  <span className="flex items-center">
                    <CalendarIcon className="w-4 h-4 mr-1" />
                    Regjistruar: {new Date(report.client.created_at).toLocaleDateString('sq-AL')}
                  </span>
                </div>
              </CardDescription>
            </div>
            <Link to="/clients">
              <Button variant="outline">Kthehu te Lista</Button>
            </Link>
          </div>
        </CardHeader>
      </Card>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="flex items-center p-6">
            <div className="p-3 rounded-full bg-blue-500 text-white mr-4">
              <FileText className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{report.total_tickets}</p>
              <p className="text-sm text-slate-600">Totali Tiketave</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <div className="p-3 rounded-full bg-orange-500 text-white mr-4">
              <Activity className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{report.active_tickets}</p>
              <p className="text-sm text-slate-600">Aktive</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <div className="p-3 rounded-full bg-green-500 text-white mr-4">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{report.completed_tickets}</p>
              <p className="text-sm text-slate-600">Përfunduar</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="flex items-center p-6">
            <div className="p-3 rounded-full bg-purple-500 text-white mr-4">
              <TrendingUp className="w-6 h-6" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">€{report.total_revenue.toFixed(2)}</p>
              <p className="text-sm text-slate-600">Totali Shitjeve</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Tickets */}
      <Card>
        <CardHeader>
          <CardTitle>Tiketat e Klientit</CardTitle>
          <CardDescription>Filtroni dhe shikoni të gjitha tiketat</CardDescription>
          
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
            <div>
              <Label>Statusi</Label>
              <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Të gjitha" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Të gjitha</SelectItem>
                  {Object.entries(defaultStatusMap).map(([key, value]) => (
                    <SelectItem key={key} value={key}>{value.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>ID Tiketi</Label>
              <Input
                placeholder="p.sh. IT123456"
                value={filters.ticketId}
                onChange={(e) => handleFilterChange('ticketId', e.target.value)}
              />
            </div>
            
            <div>
              <Label>Nga Data</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </div>
            
            <div>
              <Label>Deri Data</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <Button onClick={applyFilters}>
              <Filter className="w-4 h-4 mr-2" />
              Apliko Filtrat
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {report.tickets.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Nuk ka tiket sipas filtrave të zgjedhura</p>
          ) : (
            <div className="space-y-4">
              {report.tickets.map((ticket) => (
                <div key={ticket.id} className="border rounded-lg p-4 hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <Badge variant="outline" className="font-mono">
                        {ticket.ticket_number}
                      </Badge>
                      <div>
                        <p className="font-medium">
                          {ticket.paisja && `${ticket.paisja} ${ticket.marka || ''} ${ticket.modeli || ''}`.trim()}
                        </p>
                        <p className="text-sm text-slate-500">
                          Regjistruar: {new Date(ticket.data_regjistrimit).toLocaleDateString('sq-AL')}
                          {ticket.data_statusit && (
                            <span className="ml-2">
                              • Status ndryshua: {new Date(ticket.data_statusit).toLocaleDateString('sq-AL')} në {new Date(ticket.data_statusit).toLocaleTimeString('sq-AL')}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      {ticket.cmimi && (
                        <Badge variant="outline" className="flex items-center">
                          <Euro className="w-3 h-3 mr-1" />
                          {ticket.cmimi}
                          {ticket.paguar && <span className="ml-1 text-green-600">✓</span>}
                        </Badge>
                      )}
                      <Badge className={`${defaultStatusMap[ticket.statusi]?.color} text-white`}>
                        {defaultStatusMap[ticket.statusi]?.label}
                      </Badge>
                    </div>
                  </div>
                  
                  {ticket.pershkrimi_problemit && (
                    <p className="text-sm text-slate-600 mt-2 bg-slate-100 p-2 rounded">
                      {ticket.pershkrimi_problemit}
                    </p>
                  )}
                  
                  {ticket.shenim_teknik && (
                    <p className="text-sm text-blue-700 mt-2 bg-blue-50 p-2 rounded">
                      <strong>Shënim:</strong> {ticket.shenim_teknik}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Activity className="w-5 h-5 mr-2" />
            Aktiviteti i Fundit
          </CardTitle>
        </CardHeader>
        <CardContent>
          {report.recent_activity.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Nuk ka aktivitet të regjistruar</p>
          ) : (
            <div className="space-y-3">
              {report.recent_activity.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between py-2 border-b last:border-b-0">
                  <div>
                    <p className="text-sm font-medium">{activity.description}</p>
                    <p className="text-xs text-slate-500">
                      {new Date(activity.timestamp).toLocaleDateString('sq-AL')} në {new Date(activity.timestamp).toLocaleTimeString('sq-AL')}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {activity.action}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Panel Component
const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState("statuses");
  const [statuses, setStatuses] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [installationServices, setInstallationServices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchConfigurations();
  }, []);

  const fetchConfigurations = async () => {
    setLoading(true);
    try {
      const [statusesRes, devicesRes, brandsRes, accessoriesRes, servicesRes] = await Promise.all([
        axios.get(`${API}/config/statuses`),
        axios.get(`${API}/config/device-types`),
        axios.get(`${API}/config/brands`),
        axios.get(`${API}/config/accessories`),
        axios.get(`${API}/config/installation-services`)
      ]);
      
      setStatuses(statusesRes.data);
      setDeviceTypes(devicesRes.data);
      setBrands(brandsRes.data);
      setAccessories(accessoriesRes.data);
      setInstallationServices(servicesRes.data);
    } catch (error) {
      toast.error("Gabim në ngarkimin e konfigurimit");
      console.error("Fetch config error:", error);
    } finally {
      setLoading(false);
    }
  };

  const addNewItem = async (type, item) => {
    try {
      await axios.post(`${API}/config/${type}`, item);
      toast.success("Artikulli u shtua me sukses");
      fetchConfigurations();
    } catch (error) {
      toast.error("Gabim në shtimin e artikullit");
      console.error("Add item error:", error);
    }
  };

  const deleteItem = async (type, itemId) => {
    try {
      await axios.delete(`${API}/config/${type}/${itemId}`);
      toast.success("Artikulli u fshi me sukses");
      fetchConfigurations();
    } catch (error) {
      toast.error("Gabim në fshirjen e artikullit");
      console.error("Delete item error:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Duke ngarkuar konfigurimin...</div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Paneli i Administratorit</CardTitle>
        <CardDescription>Menaxho konfigurimin e sistemit</CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="statuses">Statuset</TabsTrigger>
            <TabsTrigger value="devices">Paisjet</TabsTrigger>
            <TabsTrigger value="brands">Markat</TabsTrigger>
            <TabsTrigger value="accessories">Aksesorët</TabsTrigger>
            <TabsTrigger value="installation">Instalim/Format</TabsTrigger>
          </TabsList>
          
          <TabsContent value="statuses" className="space-y-4">
            <StatusManager />
          </TabsContent>
          
          <TabsContent value="installation" className="space-y-4">
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">Menaxho Shërbimet e Instalimit/Formatimit</h3>
                <p className="text-sm text-blue-700">
                  Këtu mund të shtoni dhe menaxhoni shërbimet e disponueshme për instalim dhe formatim që do të shfaqen në formën e tiketave të reja.
                </p>
              </div>
              {installationServices.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {installationServices.map((service) => (
                    <div key={service.id} className="border rounded-lg p-4 hover:border-blue-300 transition">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-slate-900">{service.name}</h4>
                          {service.description && (
                            <p className="text-sm text-slate-600 mt-1">{service.description}</p>
                          )}
                          <Badge variant="secondary" className="mt-2">{service.key}</Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem("installation-services", service.id)}
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <Button onClick={() => {
                const name = prompt("Emri i shërbimit (p.sh. Formatimi i Windows):");
                const description = prompt("Përshkrimi (opsionale):");
                if (name) {
                  addNewItem("installation-services", { name, description });
                }
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Shto Shërbim të Ri
              </Button>
            </div>
          </TabsContent>
          
          <TabsContent value="devices" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Llojet e Paisjeve</h3>
              <Button onClick={() => {
                const key = prompt("Çelësi i paisjes (p.sh. scanner):");
                const label = prompt("Etiketa e paisjes (p.sh. Scanner):");
                if (key && label) {
                  addNewItem("device-types", { key, label });
                }
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Shto Paisje
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {deviceTypes.map((device) => (
                <div key={device.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <span>{device.label}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteItem("device-types", device.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="brands" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Markat</h3>
              <Button onClick={() => {
                const name = prompt("Emri i markës:");
                if (name) {
                  addNewItem("brands", { name });
                }
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Shto Markë
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {brands.map((brand) => (
                <div key={brand.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <span>{brand.name}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteItem("brands", brand.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="accessories" className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Aksesorët</h3>
              <Button onClick={() => {
                const name = prompt("Emri i aksesorit:");
                if (name) {
                  addNewItem("accessories", { name });
                }
              }}>
                <Plus className="w-4 h-4 mr-2" />
                Shto Aksesori
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {accessories.map((accessory) => (
                <div key={accessory.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <span>{accessory.name}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => deleteItem("accessories", accessory.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

// Photo Capture Component
const PhotoCapture = ({ onPhotoCapture, capturedPhoto }) => {
  const [isCapturing, setIsCapturing] = useState(false);
  const videoRef = React.useRef(null);
  const canvasRef = React.useRef(null);
  const fileInputRef = React.useRef(null);

  const startCamera = async () => {
    try {
      setIsCapturing(true);
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Prefer back camera
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error("Nuk mund të hap kamerën");
      setIsCapturing(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsCapturing(false);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const canvas = canvasRef.current;
      const video = videoRef.current;
      
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      
      const photoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
      onPhotoCapture(photoDataUrl);
      stopCamera();
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        onPhotoCapture(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-4">
      <Label>Foto e Paisjes (opsionale)</Label>
      
      {!capturedPhoto && !isCapturing && (
        <div className="flex space-x-2">
          <Button type="button" variant="outline" onClick={startCamera}>
            <Camera className="w-4 h-4 mr-2" />
            Bëj Foto
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="w-4 h-4 mr-2" />
            Ngarko Foto
          </Button>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {isCapturing && (
        <div className="space-y-2">
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline
            className="w-full max-w-md border rounded"
          />
          <div className="flex space-x-2">
            <Button type="button" onClick={capturePhoto}>
              <Camera className="w-4 h-4 mr-2" />
              Bëj Fotografi
            </Button>
            <Button type="button" variant="outline" onClick={stopCamera}>
              Anulo
            </Button>
          </div>
        </div>
      )}

      <canvas ref={canvasRef} className="hidden" />

      {capturedPhoto && (
        <div className="space-y-2">
          <img 
            src={capturedPhoto} 
            alt="Captured device" 
            className="w-full max-w-md border rounded"
          />
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onPhotoCapture(null)}
          >
            <X className="w-4 h-4 mr-2" />
            Hiq Foton
          </Button>
        </div>
      )}
    </div>
  );
};

// User Management Component
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    telefoni: "",
    password: "",
    role: "operator"
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      toast.error("Gabim në ngarkimin e përdoruesve");
      console.error("Fetch users error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await axios.put(`${API}/users/${editingUser.id}`, formData);
        toast.success("Përdoruesi u përditësua me sukses");
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success("Përdoruesi u krijua me sukses");
      }
      
      setIsModalOpen(false);
      setEditingUser(null);
      setFormData({ username: "", email: "", telefoni: "", password: "", role: "operator" });
      fetchUsers();
    } catch (error) {
      toast.error("Gabim në ruajtjen e përdoruesit");
      console.error("Save user error:", error);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      telefoni: user.telefoni || "",
      password: "",
      role: user.role
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (userId) => {
    if (confirm("Jeni i sigurt që dëshironi ta fshini këtë përdorues?")) {
      try {
        await axios.delete(`${API}/users/${userId}`);
        toast.success("Përdoruesi u fshi me sukses");
        fetchUsers();
      } catch (error) {
        toast.error("Gabim në fshirjen e përdoruesit");
        console.error("Delete user error:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Duke ngarkuar përdoruesit...</div>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Menaxhimi i Përdoruesve</CardTitle>
              <CardDescription>Krijo dhe menaxho përdoruesit e sistemit</CardDescription>
            </div>
            <Button onClick={() => setIsModalOpen(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Përdorues i Ri
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Nuk ka përdorues të regjistruar</p>
          ) : (
            <div className="space-y-4">
              {users.map((user) => (
                <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h3 className="font-semibold text-slate-900">{user.username}</h3>
                    <p className="text-sm text-slate-600">{user.email}</p>
                    {user.telefoni && (
                      <p className="text-sm text-slate-500">📞 {user.telefoni}</p>
                    )}
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                        <Shield className="w-3 h-3 mr-1" />
                        {user.role === 'admin' ? 'Administrator' : 'Operator'}
                      </Badge>
                      <Badge variant={user.active ? 'outline' : 'destructive'}>
                        {user.active ? 'Aktiv' : 'Joaktiv'}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(user)}>
                      <Edit3 className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(user.id)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingUser ? "Përditëso Përdorues" : "Përdorues i Ri"}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="username">Përdoruesi</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  required
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>

              <div>
                <Label htmlFor="telefoni">Telefoni</Label>
                <Input
                  id="telefoni"
                  type="tel"
                  placeholder="+355 XX XXX XXXX"
                  value={formData.telefoni}
                  onChange={(e) => setFormData(prev => ({ ...prev, telefoni: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="password">
                  {editingUser ? "Fjalëkalimi i Ri (lër bosh për ta mbajtur të njëjtin)" : "Fjalëkalimi"}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required={!editingUser}
                />
              </div>

              <div>
                <Label htmlFor="role">Roli</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operator">Operator (Regjistrim & Status)</SelectItem>
                    <SelectItem value="admin">Administrator (Të gjitha)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex space-x-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditingUser(null);
                    setFormData({ username: "", email: "", password: "", role: "operator" });
                  }}
                  className="flex-1"
                >
                  Anulo
                </Button>
                <Button type="submit" className="flex-1">
                  {editingUser ? "Përditëso" : "Krijo"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

// Client Edit Modal Component
const ClientEditModal = ({ client, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    emri: "",
    telefoni: "",
    email: "",
    adresa: ""
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        emri: client.emri || "",
        telefoni: client.telefoni || "",
        email: client.email || "",
        adresa: client.adresa || ""
      });
    }
  }, [client]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.put(`${API}/clients/${client.id}`, formData);
      toast.success("Klienti u përditësua me sukses");
      onUpdate();
      onClose();
    } catch (error) {
      toast.error("Gabim në përditësimin e klientit");
      console.error("Update client error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">
          Përditëso Klient - {client.emri}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="emri">Emri</Label>
            <Input
              id="emri"
              value={formData.emri}
              onChange={(e) => setFormData(prev => ({ ...prev, emri: e.target.value }))}
              required
            />
          </div>

          <div>
            <Label htmlFor="telefoni">Telefoni</Label>
            <Input
              id="telefoni"
              value={formData.telefoni}
              onChange={(e) => setFormData(prev => ({ ...prev, telefoni: e.target.value }))}
              required
            />
          </div>

          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            />
          </div>

          <div>
            <Label htmlFor="adresa">Adresa</Label>
            <Textarea
              id="adresa"
              value={formData.adresa}
              onChange={(e) => setFormData(prev => ({ ...prev, adresa: e.target.value }))}
              rows={3}
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Anulo
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? "Duke përditësuar..." : "Përditëso"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Client Search Component
const ClientSearch = ({ onSelectClient, selectedClient }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      searchClients();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const searchClients = async () => {
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/clients/search?q=${encodeURIComponent(searchTerm)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label>Kërko Klient Ekzistues (opsional)</Label>
      <div className="relative">
        <Input
          placeholder="Shkruaj emrin ose numrin e telefonit..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pr-10"
        />
        <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
      </div>
      
      {searchResults.length > 0 && (
        <div className="border rounded-md bg-white shadow-sm max-h-40 overflow-y-auto">
          {searchResults.map((client) => (
            <button
              key={client.id}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0"
              onClick={() => {
                onSelectClient(client);
                setSearchTerm(`${client.emri} - ${client.telefoni}`);
                setSearchResults([]);
              }}
            >
              <div className="font-medium">{client.emri}</div>
              <div className="text-sm text-slate-500">{client.telefoni}</div>
            </button>
          ))}
        </div>
      )}
      
      {selectedClient && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium text-green-800">{selectedClient.emri}</div>
              <div className="text-sm text-green-600">{selectedClient.telefoni}</div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onSelectClient(null);
                setSearchTerm("");
              }}
            >
              Hiq
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Dashboard Component with Auto-refresh
const Dashboard = () => {
  const { user } = useAuth(); // Get user from AuthContext
  const [dashboardData, setDashboardData] = useState(null);
  const [statusMap, setStatusMap] = useState(defaultStatusMap);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [financialSummary, setFinancialSummary] = useState(null);
  
  // Client search state
  const [clientSearchTerm, setClientSearchTerm] = useState("");
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientSearchResults, setShowClientSearchResults] = useState(false);
  const [loadingClientSearch, setLoadingClientSearch] = useState(false);
  
  // Filtered tickets modal state
  const [showFilteredTickets, setShowFilteredTickets] = useState(false);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [loadingTickets, setLoadingTickets] = useState(false);
  
  // Individual ticket modal state
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loadingTicketDetail, setLoadingTicketDetail] = useState(false);
  
  // Price editing state
  const [editingPrice, setEditingPrice] = useState(false);
  const [tempPrice, setTempPrice] = useState('');
  const [savingPrice, setSavingPrice] = useState(false);
  
  // Status change form state
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [statusFormData, setStatusFormData] = useState({
    cmimi: '',
    paguar: false,
    pjese_percjellse: '',
    riparime_shtese: ''
  });
  
  // Recent tickets state
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const [ticketDateFilter, setTicketDateFilter] = useState('all');
  const [recentTicketsLimit, setRecentTicketsLimit] = useState(10);

  useEffect(() => {
    fetchDashboardData();
    fetchFinancialSummary();
  }, []);

  // Auto-refresh functionality for dashboard
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchDashboardData();
      fetchFinancialSummary();
    }, 8000); // Refresh every 8 seconds for dashboard

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Client search functionality
  useEffect(() => {
    if (clientSearchTerm.length >= 2) {
      searchClients();
    } else {
      setClientSearchResults([]);
      setShowClientSearchResults(false);
    }
  }, [clientSearchTerm]);

  // Close client search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showClientSearchResults && !event.target.closest('.client-search-container')) {
        setShowClientSearchResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showClientSearchResults]);

  // ESC key listener for modals
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showFilteredTickets) {
          setShowFilteredTickets(false);
        } else if (showTicketDetail) {
          setShowTicketDetail(false);
        } else if (showClientSearchResults) {
          setShowClientSearchResults(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showFilteredTickets, showTicketDetail, showClientSearchResults]);

  const searchClients = async () => {
    setLoadingClientSearch(true);
    try {
      const response = await axios.get(`${API}/dashboard/clients/search?q=${encodeURIComponent(clientSearchTerm)}`);
      setClientSearchResults(response.data);
      setShowClientSearchResults(response.data.length > 0);
    } catch (error) {
      console.error("Client search error:", error);
      setClientSearchResults([]);
    } finally {
      setLoadingClientSearch(false);
    }
  };

  const fetchFilteredRecentTickets = async () => {
    try {
      let url = `${API}/tickets`;
      const params = new URLSearchParams();
      
      if (ticketStatusFilter !== 'all') {
        params.append('statusi', ticketStatusFilter);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      let tickets = response.data;
      
      // Apply date filter
      if (ticketDateFilter !== 'all') {
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const weekAgo = new Date(today);
        weekAgo.setDate(weekAgo.getDate() - 7);
        
        tickets = tickets.filter(ticket => {
          const ticketDate = new Date(ticket.data_regjistrimit);
          switch (ticketDateFilter) {
            case 'today':
              return ticketDate >= today;
            case 'yesterday':
              return ticketDate >= yesterday && ticketDate < today;
            case 'week':
              return ticketDate >= weekAgo;
            default:
              return true;
          }
        });
      }
      
      // Limit to specified number
      tickets = tickets.slice(0, recentTicketsLimit);
      
      return tickets;
    } catch (error) {
      console.error("Fetch filtered tickets error:", error);
      return [];
    }
  };

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`);
      const dashboardData = response.data;
      
      // Get filtered recent tickets instead of default
      const filteredRecentTickets = await fetchFilteredRecentTickets();
      dashboardData.recent_tickets = filteredRecentTickets;
      
      setDashboardData(dashboardData);
      
      // Create dynamic status map
      const dynamicStatusMap = {};
      dashboardData.statuses?.forEach(status => {
        const iconMap = {
          Clock: Clock,
          Wrench: Wrench,
          TestTube: TestTube,
          CheckCircle: CheckCircle
        };
        
        dynamicStatusMap[status.key] = {
          label: status.label,
          color: status.color,
          icon: iconMap[status.icon] || Clock
        };
      });
      setStatusMap(dynamicStatusMap);
    } catch (error) {
      toast.error("Gabim në ngarkimin e të dhënave");
      console.error("Dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFinancialSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/finances/summary`);
      setFinancialSummary(response.data);
    } catch (error) {
      console.error("Fetch financial summary error:", error);
    }
  };


  const fetchTicketsByStatus = async (status) => {
    setLoadingTickets(true);
    setFilterStatus(status);
    
    try {
      const params = status === 'all' ? '' : `?statusi=${status}`;
      const response = await axios.get(`${API}/tickets${params}`);
      setFilteredTickets(response.data);
      setShowFilteredTickets(true);
    } catch (error) {
      toast.error('Gabim në ngarkimin e tiketave');
      console.error('Fetch tickets by status error:', error);
    } finally {
      setLoadingTickets(false);
    }
  };

  // Refresh tickets when filters change
  useEffect(() => {
    if (dashboardData) {
      const refreshTickets = async () => {
        const filteredRecentTickets = await fetchFilteredRecentTickets();
        setDashboardData(prev => ({ ...prev, recent_tickets: filteredRecentTickets }));
      };
      refreshTickets();
    }
  }, [ticketStatusFilter, ticketDateFilter, recentTicketsLimit]);

  const fetchTicketDetails = async (ticketId) => {
    setLoadingTicketDetail(true);
    
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`);
      setSelectedTicket(response.data);
      setShowTicketDetail(true);
      setShowFilteredTickets(false); // Close the filtered tickets modal
    } catch (error) {
      toast.error('Gabim në ngarkimin e detajeve të tiketit');
      console.error('Fetch ticket details error:', error);
    } finally {
      setLoadingTicketDetail(false);
    }
  };

  const handleSendSMS = async (ticketId, phoneNumber, ticketNumber) => {
    try {
      const response = await axios.post(`${API}/tickets/${ticketId}/send-sms`, {
        message: `Përshëndetje! Tiketi juaj ${ticketNumber} është gati për marrje. IT Solution`
      });
      
      toast.success('SMS u dërgua me sukses');
    } catch (error) {
      toast.error('Gabim në dërgimin e SMS');
      console.error('Send SMS error:', error);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    if (newStatus === 'gati') {
      // Hap form-in për të plotësuar detajet
      setStatusFormData({
        cmimi: selectedTicket.cmimi || '',
        paguar: selectedTicket.paguar || false,
        pjese_percjellse: selectedTicket.pjese_percjellse || '',
        riparime_shtese: selectedTicket.riparime_shtese || ''
      });
      setShowStatusForm(true);
    } else {
      // Ndrysho statusin direkt për status të tjerë
      try {
        const response = await axios.put(`${API}/tickets/${ticketId}/status`, {
          statusi: newStatus
        });
        
        toast.success('Statusi u ndryshua me sukses');
        setSelectedTicket(prev => ({ ...prev, statusi: newStatus }));
        fetchDashboardData();
      } catch (error) {
        toast.error('Gabim në ndryshimin e statusit');
        console.error('Status change error:', error);
      }
    }
  };

  const handleGatiFormSubmit = async () => {
    try {
      const response = await axios.put(`${API}/tickets/${selectedTicket.id}/status`, {
        statusi: 'gati',
        cmimi: parseFloat(statusFormData.cmimi) || null,
        paguar: statusFormData.paguar,
        pjese_percjellse: statusFormData.pjese_percjellse || null,
        riparime_shtese: statusFormData.riparime_shtese || null
      });
      
      toast.success('Statusi u ndryshua në Gati me sukses');
      setSelectedTicket(prev => ({ 
        ...prev, 
        statusi: 'gati',
        cmimi: parseFloat(statusFormData.cmimi) || null,
        paguar: statusFormData.paguar,
        pjese_percjellse: statusFormData.pjese_percjellse || null,
        riparime_shtese: statusFormData.riparime_shtese || null
      }));
      setShowStatusForm(false);
      fetchDashboardData();
    } catch (error) {
      toast.error('Gabim në ndryshimin e statusit');
      console.error('Status change error:', error);
    }
  };

  // Price editing functions
  const handlePriceEdit = () => {
    setTempPrice(selectedTicket.cmimi || '0');
    setEditingPrice(true);
  };

  const handlePriceSave = async () => {
    if (!selectedTicket?.id) return;
    
    setSavingPrice(true);
    try {
      const response = await axios.put(`${API}/tickets/${selectedTicket.id}/payment`, {
        ticket_id: selectedTicket.id,
        amount: parseFloat(tempPrice) || 0,
        payment_method: 'Manual Update',
        notes: `Çmimi ndryshuar nga €${selectedTicket.cmimi || 0} në €${tempPrice} nga ${user?.emri || 'Admin'}`
      });

      if (response.data.success) {
        const oldAmount = selectedTicket.cmimi || 0;
        const newAmount = parseFloat(tempPrice) || 0;
        const changeAmount = newAmount - oldAmount;
        const changeText = changeAmount > 0 ? `+€${changeAmount.toFixed(2)}` : `€${changeAmount.toFixed(2)}`;
        
        toast.success(`✅ Çmimi u përditësua me sukses! ${changeText}`);
        
        // Update selected ticket
        setSelectedTicket(prev => ({ 
          ...prev, 
          cmimi: newAmount,
          payment_amount: newAmount
        }));
        
        setEditingPrice(false);
        fetchDashboardData(); // Refresh dashboard data
      }
    } catch (error) {
      toast.error('❌ Gabim në ruajtjen e çmimit');
      console.error('Price update error:', error);
    } finally {
      setSavingPrice(false);
    }
  };

  const handlePriceCancel = () => {
    setTempPrice('');
    setEditingPrice(false);
  };

  // Receipt Printing Functions
  const generateReceiptHTML = (ticket, format = 'a4') => {
    const isThermal = format === 'thermal';
    const currentDate = new Date().toLocaleDateString('sq-AL');
    const currentTime = new Date().toLocaleTimeString('sq-AL');
    
    return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Faturë - ${ticket.ticket_number}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Courier New', monospace;
      ${isThermal ? 'width: 80mm;' : 'width: 210mm;'}
      margin: 0 auto;
      padding: ${isThermal ? '10px' : '20mm'};
      background: white;
    }
    
    .receipt-container {
      ${isThermal ? 'font-size: 11px;' : 'font-size: 12px;'}
      line-height: 1.4;
    }
    
    .header {
      text-align: center;
      margin-bottom: ${isThermal ? '15px' : '25px'};
      border-bottom: 2px solid #000;
      padding-bottom: ${isThermal ? '10px' : '15px'};
    }
    
    .logo {
      ${isThermal ? 'max-width: 60px;' : 'max-width: 100px;'}
      height: auto;
      margin-bottom: 10px;
    }
    
    .company-name {
      font-size: ${isThermal ? '16px' : '20px'};
      font-weight: bold;
      margin-bottom: 5px;
    }
    
    .company-info {
      font-size: ${isThermal ? '10px' : '11px'};
      color: #333;
    }
    
    .section {
      margin-bottom: ${isThermal ? '12px' : '18px'};
      border-bottom: 1px dashed #666;
      padding-bottom: ${isThermal ? '8px' : '12px'};
    }
    
    .section-title {
      font-weight: bold;
      font-size: ${isThermal ? '12px' : '14px'};
      margin-bottom: 5px;
      text-transform: uppercase;
    }
    
    .info-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 3px;
      ${isThermal ? 'font-size: 10px;' : 'font-size: 11px;'}
    }
    
    .label {
      font-weight: bold;
    }
    
    .value {
      text-align: right;
      ${isThermal ? 'max-width: 50%;' : 'max-width: 60%;'}
      word-wrap: break-word;
    }
    
    .total-section {
      margin-top: ${isThermal ? '15px' : '25px'};
      padding-top: ${isThermal ? '10px' : '15px'};
      border-top: 2px solid #000;
    }
    
    .total-row {
      display: flex;
      justify-content: space-between;
      font-size: ${isThermal ? '14px' : '16px'};
      font-weight: bold;
      margin-bottom: 5px;
    }
    
    .payment-status {
      text-align: center;
      padding: ${isThermal ? '8px' : '12px'};
      margin: ${isThermal ? '10px 0' : '15px 0'};
      border: 2px solid ${ticket.paguar ? '#22c55e' : '#ef4444'};
      background: ${ticket.paguar ? '#f0fdf4' : '#fef2f2'};
      font-weight: bold;
    }
    
    .footer {
      text-align: center;
      margin-top: ${isThermal ? '15px' : '25px'};
      padding-top: ${isThermal ? '10px' : '15px'};
      border-top: 1px solid #666;
      font-size: ${isThermal ? '9px' : '10px'};
      color: #666;
    }
    
    @media print {
      body {
        ${isThermal ? 'width: 80mm;' : 'width: auto;'}
        margin: 0;
        padding: ${isThermal ? '5mm' : '10mm'};
      }
      
      @page {
        size: ${isThermal ? '80mm auto' : 'A4'};
        margin: ${isThermal ? '0' : '10mm'};
      }
    }
  </style>
</head>
<body>
  <div class="receipt-container">
    <!-- Header -->
    <div class="header">
      <img src="${LOGO_URL}" alt="IT Solution" class="logo" />
      <div class="company-name">IT SOLUTION</div>
      <div class="company-info">
        Adresa: Rruga Kryesore, Tiranë<br/>
        Tel: +355 69 XXX XXXX<br/>
        Email: info@itsolution.al
      </div>
    </div>
    
    <!-- Receipt Info -->
    <div class="section">
      <div class="section-title">Faturë</div>
      <div class="info-row">
        <span class="label">Nr. Tiketi:</span>
        <span class="value">${ticket.ticket_number}</span>
      </div>
      <div class="info-row">
        <span class="label">Data:</span>
        <span class="value">${currentDate} ${currentTime}</span>
      </div>
      <div class="info-row">
        <span class="label">Data Regjistrimit:</span>
        <span class="value">${new Date(ticket.data_regjistrimit).toLocaleDateString('sq-AL')}</span>
      </div>
    </div>
    
    <!-- Client Info -->
    <div class="section">
      <div class="section-title">Të Dhënat e Klientit</div>
      <div class="info-row">
        <span class="label">Emri:</span>
        <span class="value">${ticket.client_emri}</span>
      </div>
      <div class="info-row">
        <span class="label">Telefoni:</span>
        <span class="value">${ticket.client_telefoni}</span>
      </div>
      ${ticket.client_email ? `
      <div class="info-row">
        <span class="label">Email:</span>
        <span class="value">${ticket.client_email}</span>
      </div>` : ''}
    </div>
    
    <!-- Device Info -->
    <div class="section">
      <div class="section-title">Të Dhënat e Paisjes</div>
      ${ticket.paisja ? `
      <div class="info-row">
        <span class="label">Paisja:</span>
        <span class="value">${ticket.paisja}</span>
      </div>` : ''}
      ${ticket.marka ? `
      <div class="info-row">
        <span class="label">Marka:</span>
        <span class="value">${ticket.marka}</span>
      </div>` : ''}
      ${ticket.modeli ? `
      <div class="info-row">
        <span class="label">Modeli:</span>
        <span class="value">${ticket.modeli}</span>
      </div>` : ''}
      ${ticket.numri_serik ? `
      <div class="info-row">
        <span class="label">Serial:</span>
        <span class="value">${ticket.numri_serik}</span>
      </div>` : ''}
    </div>
    
    <!-- Service Details -->
    ${ticket.pershkrimi_problemit ? `
    <div class="section">
      <div class="section-title">Përshkrimi i Problemit</div>
      <div style="margin-top: 5px; font-size: ${isThermal ? '10px' : '11px'};">
        ${ticket.pershkrimi_problemit}
      </div>
    </div>` : ''}
    
    ${ticket.shenim_teknik ? `
    <div class="section">
      <div class="section-title">Shënim Teknik</div>
      <div style="margin-top: 5px; font-size: ${isThermal ? '10px' : '11px'};">
        ${ticket.shenim_teknik}
      </div>
    </div>` : ''}
    
    ${ticket.riparime_shtese ? `
    <div class="section">
      <div class="section-title">Riparime Shtesë</div>
      <div style="margin-top: 5px; font-size: ${isThermal ? '10px' : '11px'};">
        ${ticket.riparime_shtese}
      </div>
    </div>` : ''}
    
    ${ticket.pjese_percjellse ? `
    <div class="section">
      <div class="section-title">Pjesë të Përdorura</div>
      <div style="margin-top: 5px; font-size: ${isThermal ? '10px' : '11px'};">
        ${ticket.pjese_percjellse}
      </div>
    </div>` : ''}
    
    <!-- Total -->
    ${ticket.cmimi && ticket.cmimi > 0 ? `
    <div class="total-section">
      <div class="total-row">
        <span>TOTALI:</span>
        <span>€${ticket.cmimi}</span>
      </div>
    </div>
    
    <div class="payment-status">
      ${ticket.paguar ? '✓ I PAGUAR' : 'NUK ËSHTË PAGUAR'}
    </div>` : `
    <div class="payment-status" style="background: #f3f4f6; border-color: #9ca3af;">
      SHËRBIM PA PAGESË
    </div>`}
    
    <!-- Footer -->
    <div class="footer">
      Faleminderit për besimin!<br/>
      Për pyetje apo probleme, ju lutemi na kontaktoni.
    </div>
  </div>
</body>
</html>`;
  };

  const handlePrintReceipt = (format) => {
    if (!selectedTicket) {
      toast.error("Nuk ka tiket të zgjedhur");
      return;
    }
    
    try {
      const receiptHTML = generateReceiptHTML(selectedTicket, format);
      
      if (format === 'thermal') {
        // For thermal: Open in new window and trigger print automatically
        const printWindow = window.open('', '_blank', 'width=300,height=600');
        printWindow.document.write(receiptHTML);
        printWindow.document.close();
        
        // Auto-trigger print after content loads
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            // Close window after printing (optional)
            setTimeout(() => {
              printWindow.close();
            }, 1000);
          }, 500);
        };
        
        toast.success('Duke printuar në printer termal...');
      } else {
        // For A4: Use iframe method
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = 'none';
        
        document.body.appendChild(iframe);
        
        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(receiptHTML);
        doc.close();
        
        // Wait for images to load then print
        iframe.contentWindow.onload = () => {
          setTimeout(() => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            
            // Remove iframe after printing
            setTimeout(() => {
              document.body.removeChild(iframe);
            }, 1000);
          }, 500);
        };
        
        toast.success('Duke printuar në A4...');
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error("Gabim në printim");
    }
  };

  const handlePaymentStatusChange = async (newStatus) => {
    if (!selectedTicket?.id) return;
    
    try {
      // Handle three states: paid, unpaid, no_payment
      let paguar = false;
      let cmimi = selectedTicket.cmimi;
      
      if (newStatus === "paid") {
        paguar = true;
      } else if (newStatus === "unpaid") {
        paguar = false;
      } else if (newStatus === "no_payment") {
        paguar = false;
        cmimi = 0;
      }
      
      // Update payment status using existing endpoint
      const response = await axios.put(
        `${API}/tickets/${selectedTicket.id}/payment-status`,
        null,
        { params: { paguar: paguar } }
      );
      
      if (response.data.success) {
        toast.success("Statusi i pagesës u përditësua me sukses");
        setSelectedTicket(prev => ({ 
          ...prev, 
          paguar: paguar,
          cmimi: cmimi
        }));
        // Refresh dashboard if on dashboard
        if (typeof fetchDashboardData === 'function') {
          fetchDashboardData();
        }
      }
    } catch (error) {
      console.error('Update payment status error:', error);
      toast.error("Gabim në përditësimin e statusit të pagesës");
    }
  };

  const handleDownloadReceipt = () => {
    if (!selectedTicket) {
      toast.error("Nuk ka tiket të zgjedhur");
      return;
    }
    
    try {
      const receiptHTML = generateReceiptHTML(selectedTicket, 'a4');
      
      // Create blob and download
      const blob = new Blob([receiptHTML], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `fature_${selectedTicket.ticket_number}_${new Date().getTime()}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success("Fatura u shkarkua me sukses");
    } catch (error) {
      console.error('Download error:', error);
      toast.error("Gabim në shkarkimin e faturës");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Duke ngarkuar...</div>
      </div>
    );
  }

  const stats = dashboardData?.stats || {};
  const recentTickets = dashboardData?.recent_tickets || [];

  return (
    <div className="space-y-6">
      {/* Client Search Bar */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center text-lg">
                <Search className="w-5 h-5 mr-2" />
                Kërko Klientë
              </CardTitle>
              <CardDescription>Shkruaj emrin ose numrin e telefonit për të gjetur klientin</CardDescription>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/new-ticket">
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Tiket e re
                </Button>
              </Link>
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="dashboard-auto-refresh" 
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                />
                <Label htmlFor="dashboard-auto-refresh" className="text-sm">
                  Auto-refresh (8s)
                </Label>
                {autoRefresh && (
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                )}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="relative client-search-container">
            <Input
              placeholder="Shkruaj emrin apo numrin e telefonit të klientit..."
              value={clientSearchTerm}
              onChange={(e) => setClientSearchTerm(e.target.value)}
              className="pr-10"
            />
            <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            
            {/* Search Results Dropdown */}
            {showClientSearchResults && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                {loadingClientSearch ? (
                  <div className="p-3 text-center text-slate-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mx-auto"></div>
                  </div>
                ) : clientSearchResults.length > 0 ? (
                  clientSearchResults.map((client) => (
                    <Link 
                      key={client.id} 
                      to={`/clients/${client.id}/report`}
                      className="block p-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      onClick={() => {
                        setShowClientSearchResults(false);
                        setClientSearchTerm("");
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="font-medium text-slate-900">{client.emri}</p>
                          <p className="text-sm text-slate-600">{client.telefoni}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="text-xs">
                            {client.status_info}
                          </Badge>
                        </div>
                      </div>
                    </Link>
                  ))
                ) : (
                  <div className="p-3 text-center text-slate-500">
                    Nuk u gjet asnjë klient
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Object.entries(statusMap).map(([key, value]) => {
          const Icon = value.icon;
          const count = dashboardData?.stats_by_status?.[key] || 0;
          return (
            <Card 
              key={key} 
              className="hover:shadow-lg transition-shadow cursor-pointer transform hover:scale-105"
              onClick={() => fetchTicketsByStatus(key)}
            >
              <CardContent className="flex items-center p-4">
                <div className={`p-2 rounded-full ${value.color} text-white mr-3`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xl font-bold text-slate-900">{count}</p>
                  <p className="text-xs text-slate-600">{value.label}</p>
                  <p className="text-xs text-slate-500 mt-1">Kliko për tiketa</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Financial Summary Cards - Admin Only & Smaller */}
      {financialSummary && user?.role === "admin" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
            <CardContent className="flex items-center p-4">
              <div className="p-2 rounded-full bg-green-500 text-white mr-3">
                <Euro className="w-4 h-4" />
              </div>
              <div>
                <p className="text-lg font-bold text-green-800">{financialSummary.today_revenue}€</p>
                <p className="text-xs text-green-600">SOT</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="flex items-center p-4">
              <div className="p-2 rounded-full bg-blue-500 text-white mr-3">
                <TrendingUp className="w-4 h-4" />
              </div>
              <div>
                <p className="text-lg font-bold text-blue-800">{financialSummary.month_revenue}€</p>
                <p className="text-xs text-blue-600">Muaji</p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="flex items-center p-4">
              <div className="p-2 rounded-full bg-yellow-500 text-white mr-3">
                <Clock className="w-4 h-4" />
              </div>
              <div>
                <p className="text-lg font-bold text-yellow-800">{financialSummary.pending_revenue}€</p>
                <p className="text-xs text-yellow-600">Në pritje</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recent Tickets - Improved */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Tiketat e Fundit</CardTitle>
              <CardDescription>Lista e tiketave më të reja - {recentTicketsLimit} tiketa</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                <Label className="text-sm">Statusi:</Label>
                <Select value={ticketStatusFilter} onValueChange={setTicketStatusFilter}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të gjitha</SelectItem>
                    {Object.entries(statusMap).map(([key, value]) => (
                      <SelectItem key={key} value={key}>{value.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Label className="text-sm">Data:</Label>
                <Select value={ticketDateFilter} onValueChange={setTicketDateFilter}>
                  <SelectTrigger className="w-28">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të gjitha</SelectItem>
                    <SelectItem value="today">Sot</SelectItem>
                    <SelectItem value="yesterday">Dje</SelectItem>
                    <SelectItem value="week">Java e fundit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Label className="text-sm">Limit:</Label>
                <Select value={recentTicketsLimit.toString()} onValueChange={(value) => setRecentTicketsLimit(parseInt(value))}>
                  <SelectTrigger className="w-16">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="15">15</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {recentTickets.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Nuk ka tiket sipas filtrave të zgjedhura</p>
          ) : (
            <div className="space-y-2">
              {recentTickets.map((ticket) => (
                <div key={ticket.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Badge 
                        variant="outline" 
                        className="font-mono text-xs cursor-pointer hover:bg-blue-100 transition-colors"
                        onClick={() => fetchTicketDetails(ticket.id)}
                        title="Kliko për të parë detajet"
                      >
                        {ticket.ticket_number}
                      </Badge>
                      <span className="font-medium text-sm text-slate-900">{ticket.client_emri}</span>
                      {ticket.paisja && (
                        <>
                          <span className="text-slate-400">•</span>
                          <span className="text-xs text-slate-600">{ticket.paisja} {ticket.marka}</span>
                        </>
                      )}
                    </div>
                    {ticket.pershkrimi_problemit && (
                      <p className="text-xs text-slate-500 mt-1 truncate">{ticket.pershkrimi_problemit}</p>
                    )}
                    <p className="text-xs text-slate-400 mt-1">
                      {new Date(ticket.data_regjistrimit).toLocaleDateString('sq-AL')} 
                      {ticket.data_statusit && (
                        <span className="ml-2">
                          • Status: {new Date(ticket.data_statusit).toLocaleDateString('sq-AL')}
                        </span>
                      )}
                    </p>
                  </div>
                  <Badge className={`${statusMap[ticket.statusi]?.color} text-white text-xs`}>
                    {statusMap[ticket.statusi]?.label}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filtered Tickets Modal */}
      {showFilteredTickets && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowFilteredTickets(false);
            }
          }}
        >
          <Card className="w-full max-w-4xl max-h-[80vh] overflow-hidden">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>
                  Tiketat me Status: {statusMap[filterStatus]?.label}
                </CardTitle>
                <Button variant="ghost" onClick={() => setShowFilteredTickets(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[60vh]">
              {loadingTickets ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Duke ngarkuar tiketat...</p>
                </div>
              ) : filteredTickets.length === 0 ? (
                <p className="text-slate-500 text-center py-8">
                  Nuk ka tikete me këtë status
                </p>
              ) : (
                <div className="space-y-4">
                  {filteredTickets.map((ticket) => (
                    <div key={ticket.id} className="border rounded-lg p-4 hover:bg-slate-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <Badge 
                              variant="outline" 
                              className="font-mono text-xs cursor-pointer hover:bg-blue-100 transition-colors"
                              onClick={() => fetchTicketDetails(ticket.id)}
                              title="Kliko për të parë detajet e tiketit"
                            >
                              {ticket.ticket_number}
                            </Badge>
                            <span className="font-medium">{ticket.client_emri}</span>
                            <span className="text-slate-500">-</span>
                            <span className="text-slate-600">{ticket.client_telefoni}</span>
                          </div>
                          {ticket.paisja && (
                            <p className="text-sm text-slate-600 mb-1">
                              Paisja: {ticket.paisja} {ticket.marka}
                            </p>
                          )}
                          {ticket.pershkrimi_problemit && (
                            <p className="text-sm text-slate-500">
                              {ticket.pershkrimi_problemit}
                            </p>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            Regjistruar: {new Date(ticket.data_regjistrimit).toLocaleDateString('sq-AL')}
                          </p>
                        </div>
                        <Badge className={`${statusMap[ticket.statusi]?.color} text-white`}>
                          {statusMap[ticket.statusi]?.label}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Individual Ticket Detail Modal */}
      {showTicketDetail && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTicketDetail(false);
            }
          }}
        >
          <Card className="w-full max-w-2xl max-h-[80vh] overflow-hidden">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center space-x-2">
                  <Badge variant="outline" className="font-mono">
                    {selectedTicket?.ticket_number}
                  </Badge>
                  <span>Detajet e Tiketit</span>
                </CardTitle>
                <Button variant="ghost" onClick={() => setShowTicketDetail(false)}>
                  <X className="w-5 h-5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handleSendSMS(selectedTicket?.id, selectedTicket?.client_telefoni, selectedTicket?.ticket_number)}
                  className="mr-2"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Dërgo SMS
                </Button>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[60vh]">
              {loadingTicketDetail ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Duke ngarkuar detajet...</p>
                </div>
              ) : selectedTicket ? (
                <div className="space-y-4">
                  {/* Client Information */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Informacionet e Klientit</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <span className="text-sm font-medium text-slate-600">Emri:</span>
                        <p className="font-semibold">{selectedTicket.client_emri}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-600">Telefoni:</span>
                        <div className="mt-1">
                          <MessagingButtons 
                            phoneNumber={selectedTicket.client_telefoni}
                            clientName={selectedTicket.client_emri}
                            ticketNumber={selectedTicket.ticket_number}
                          />
                        </div>
                      </div>
                      {selectedTicket.client_email && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Email:</span>
                          <p className="font-semibold">{selectedTicket.client_email}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Device Information */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Informacionet e Paisjes</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedTicket.paisja && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Paisja:</span>
                          <p className="font-semibold">{selectedTicket.paisja}</p>
                        </div>
                      )}
                      {selectedTicket.marka && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Marka:</span>
                          <p className="font-semibold">{selectedTicket.marka}</p>
                        </div>
                      )}
                      {selectedTicket.modeli && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Modeli:</span>
                          <p className="font-semibold">{selectedTicket.modeli}</p>
                        </div>
                      )}
                      {selectedTicket.numri_serik && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Serial Number:</span>
                          <p className="font-semibold font-mono text-sm">{selectedTicket.numri_serik}</p>
                        </div>
                      )}
                    </div>
                    
                    {selectedTicket.paisjet_percjellse && selectedTicket.paisjet_percjellse.length > 0 && (
                      <div className="mt-3">
                        <span className="text-sm font-medium text-slate-600">Paisje të përfshira:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedTicket.paisjet_percjellse.map((item, index) => (
                            <Badge key={index} variant="secondary">{item}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Problem Description */}
                  {selectedTicket.pershkrimi_problemit && (
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-2">Përshkrimi i Problemit</h3>
                      <p className="text-slate-700">{selectedTicket.pershkrimi_problemit}</p>
                    </div>
                  )}

                  {/* Installation/Formatting Section - Read Only */}
                  {(selectedTicket.formatimi || selectedTicket.ruaj_shenimet || selectedTicket.programe_shtese || selectedTicket.lista_programeve) && (
                    <div className="bg-indigo-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                        <Settings className="w-5 h-5 mr-2" />
                        Instalimi / Formatizimi
                      </h3>
                      <div className="space-y-2">
                        {selectedTicket.formatimi && (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium">Formatizimi i sistemit</span>
                          </div>
                        )}
                        {selectedTicket.ruaj_shenimet && (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium">Ruaj shënimet / të dhënat</span>
                          </div>
                        )}
                        {selectedTicket.programe_shtese && (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium">Instalim i programeve shtesë</span>
                          </div>
                        )}
                        {selectedTicket.lista_programeve && (
                          <div className="mt-2 pl-6">
                            <span className="text-xs font-medium text-slate-600 block mb-1">Programet për instalim:</span>
                            <p className="text-sm text-slate-700 bg-white p-2 rounded border border-slate-200">
                              {selectedTicket.lista_programeve}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Device Photo */}
                  {selectedTicket.device_photo_url && (
                    <div className="bg-slate-100 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                        <Camera className="w-5 h-5 mr-2" />
                        Fotografia e Paisjes
                      </h3>
                      <div className="mt-2">
                        <img 
                          src={`${API.replace('/api', '')}${selectedTicket.device_photo_url}`}
                          alt="Device" 
                          className="max-w-full h-auto rounded cursor-pointer hover:shadow-lg transition border-2 border-slate-300"
                          onClick={() => {
                            const modal = document.createElement('div');
                            modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50';
                            modal.onclick = () => modal.remove();
                            modal.innerHTML = `
                              <div class="relative max-w-4xl max-h-screen p-4">
                                <img src="${API.replace('/api', '')}${selectedTicket.device_photo_url}" 
                                     class="max-w-full max-h-screen rounded-lg" 
                                     alt="Device Photo" />
                                <button class="absolute top-6 right-6 text-white bg-red-500 rounded-full w-10 h-10 flex items-center justify-center text-2xl hover:bg-red-600">×</button>
                              </div>
                            `;
                            document.body.appendChild(modal);
                          }}
                          style={{ maxHeight: '150px' }}
                        />
                        <p className="text-xs text-slate-500 mt-1">📸 Kliko për ta zmadhuar</p>
                      </div>
                    </div>
                  )}

                  {/* Status and Dates */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Statusi dhe Datat</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <span className="text-sm font-medium text-slate-600">Statusi Aktual:</span>
                        <div className="mt-1 flex items-center space-x-2">
                          <Badge className={`${statusMap[selectedTicket.statusi]?.color} text-white`}>
                            {statusMap[selectedTicket.statusi]?.label}
                          </Badge>
                          <Select 
                            value={selectedTicket.statusi} 
                            onValueChange={(value) => handleStatusChange(selectedTicket.id, value)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue placeholder="Ndrysho statusin" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(statusMap).map(([key, status]) => (
                                <SelectItem key={key} value={key}>
                                  <div className="flex items-center space-x-2">
                                    <div className={`w-3 h-3 rounded-full ${status.color}`}></div>
                                    <span>{status.label}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-600">Data e Regjistrimit:</span>
                        <p className="font-semibold">
                          {new Date(selectedTicket.data_regjistrimit).toLocaleDateString('sq-AL')} në {new Date(selectedTicket.data_regjistrimit).toLocaleTimeString('sq-AL')}
                        </p>
                      </div>
                      {selectedTicket.data_statusit && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Statusi ndryshua:</span>
                          <p className="font-semibold">
                            {new Date(selectedTicket.data_statusit).toLocaleDateString('sq-AL')} në {new Date(selectedTicket.data_statusit).toLocaleTimeString('sq-AL')}
                          </p>
                        </div>
                      )}
                      {selectedTicket.data_perfundimit && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Data e Përfundimit:</span>
                          <p className="font-semibold">
                            {new Date(selectedTicket.data_perfundimit).toLocaleDateString('sq-AL')} në {new Date(selectedTicket.data_perfundimit).toLocaleTimeString('sq-AL')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Information */}
                  {(selectedTicket.cmimi || selectedTicket.paguar) && (
                    <div className="bg-purple-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-3">Informacioni i Pagesës</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(selectedTicket.cmimi || editingPrice) && (
                          <div>
                            <span className="text-sm font-medium text-slate-600">Çmimi:</span>
                            {editingPrice ? (
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex items-center">
                                  <span className="text-lg">€</span>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={tempPrice}
                                    onChange={(e) => setTempPrice(e.target.value)}
                                    className="w-24 h-8 text-lg font-semibold text-right px-1 ml-1"
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') handlePriceSave();
                                      if (e.key === 'Escape') handlePriceCancel();
                                    }}
                                  />
                                </div>
                                <Button
                                  size="sm"
                                  onClick={handlePriceSave}
                                  disabled={savingPrice}
                                  className="h-8 px-2 bg-green-600 hover:bg-green-700"
                                >
                                  {savingPrice ? (
                                    <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                                  ) : (
                                    <Save className="w-3 h-3" />
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={handlePriceCancel}
                                  disabled={savingPrice}
                                  className="h-8 px-2"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 mt-1">
                                <p className="font-semibold text-green-600 text-lg">
                                  {selectedTicket.cmimi} €
                                </p>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={handlePriceEdit}
                                  className="h-6 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                  title="Ndrysho çmimin"
                                >
                                  <Edit3 className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        <div>
                          <span className="text-sm font-medium text-slate-600">Statusi i Pagesës:</span>
                          <div className="mt-1">
                            <Select 
                              value={
                                !selectedTicket.cmimi || selectedTicket.cmimi === 0 
                                  ? "no_payment" 
                                  : selectedTicket.paguar 
                                    ? "paid" 
                                    : "unpaid"
                              } 
                              onValueChange={(value) => handlePaymentStatusChange(value)}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="paid">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    <span>I paguar</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="unpaid">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                    <span>I papaguar</span>
                                  </div>
                                </SelectItem>
                                <SelectItem value="no_payment">
                                  <div className="flex items-center space-x-2">
                                    <div className="w-3 h-3 rounded-full bg-gray-500"></div>
                                    <span>Nuk ka Pagese</span>
                                  </div>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      
                      {/* Print Receipt Section */}
                      <div className="mt-3 pt-3 border-t border-purple-200">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-600">Printim:</span>
                          
                          <Button
                            size="sm"
                            onClick={() => handlePrintReceipt('thermal')}
                            disabled={loadingTicketDetail}
                            className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-2"
                            title="Printo në printer termal (80mm)"
                          >
                            <Receipt className="w-3 h-3" />
                            <span className="ml-1 text-xs">Termal</span>
                          </Button>
                          
                          <Button
                            size="sm" 
                            onClick={() => handlePrintReceipt('a4')}
                            disabled={loadingTicketDetail}
                            className="bg-green-600 hover:bg-green-700 text-white h-7 px-2"
                            title="Printo në A4"
                          >
                            <Printer className="w-3 h-3" />
                            <span className="ml-1 text-xs">A4</span>
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownloadReceipt()}
                            className="h-7 px-2"
                            title="Download si HTML"
                          >
                            <Download className="w-3 h-3" />
                            <span className="ml-1 text-xs">Download</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">
                  Gabim në ngarkimin e detajeve të tiketit
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Gati Status Form Modal */}
      {showStatusForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Ndrysho Statusin në Gati</CardTitle>
              <CardDescription>Plotëso informacionet e nevojshme</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="cmimi">Çmimi (€) *</Label>
                <Input
                  id="cmimi"
                  type="number"
                  step="0.01"
                  value={statusFormData.cmimi}
                  onChange={(e) => setStatusFormData(prev => ({ ...prev, cmimi: e.target.value }))}
                  placeholder="0.00"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="pjese_percjellse">Pjesë Percjellëse</Label>
                <Textarea
                  id="pjese_percjellse"
                  value={statusFormData.pjese_percjellse}
                  onChange={(e) => setStatusFormData(prev => ({ ...prev, pjese_percjellse: e.target.value }))}
                  placeholder="P.sh. Bateri e re, RAM 8GB, Ekran i ri..."
                  rows={2}
                />
              </div>
              
              <div>
                <Label htmlFor="riparime_shtese">Riparime Shtesë</Label>
                <Textarea
                  id="riparime_shtese"
                  value={statusFormData.riparime_shtese}
                  onChange={(e) => setStatusFormData(prev => ({ ...prev, riparime_shtese: e.target.value }))}
                  placeholder="P.sh. Pastrimi i fansave..."
                  rows={2}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="paguar"
                  checked={statusFormData.paguar}
                  onCheckedChange={(checked) => setStatusFormData(prev => ({ ...prev, paguar: checked }))}
                />
                <Label htmlFor="paguar">Paguar</Label>
              </div>
              
              <div className="flex space-x-2 pt-4">
                <Button onClick={handleGatiFormSubmit} className="flex-1">
                  Ruaj
                </Button>
                <Button variant="outline" onClick={() => setShowStatusForm(false)}>
                  Anulo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Chat Notifications */}
      <ChatNotifications />
    </div>
  );
};

// New Ticket Form Component
const NewTicketForm = () => {
  const [formData, setFormData] = useState({
    client_emri: "",
    client_telefoni: "",
    client_email: "",
    paisja: "",
    marka: "",
    modeli: "",
    serial_number: "",
    pershkrimi_problemit: "",
    paisjet_percjellse: [],
    // Installation/Formatting fields
    formatimi: false,
    ruaj_shenimet: false,
    programe_shtese: false,
    lista_programeve: ""
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [brands, setBrands] = useState([]);
  const [accessories, setAccessories] = useState([]);

  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const [devicesRes, brandsRes, accessoriesRes] = await Promise.all([
        axios.get(`${API}/config/device-types`),
        axios.get(`${API}/config/brands`),
        axios.get(`${API}/config/accessories`)
      ]);
      
      setDeviceTypes(devicesRes.data);
      setBrands(brandsRes.data);
      setAccessories(accessoriesRes.data);
    } catch (error) {
      console.error("Fetch options error:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const ticketData = { 
        ...formData, 
        client_id: selectedClient?.id || null,
        device_photo_base64: capturedPhoto
      };
      
      const response = await axios.post(`${API}/tickets`, ticketData);
      toast.success(`Tiketi u krijua me sukses! Numri: ${response.data.ticket_number}`);
      
      // Reset form
      setFormData({
        client_emri: "",
        client_telefoni: "",
        client_email: "",
        paisja: "",
        marka: "",
        modeli: "",
        serial_number: "",
        pershkrimi_problemit: "",
        paisjet_percjellse: [],
        formatimi: false,
        ruaj_shenimet: false,
        programe_shtese: false,
        lista_programeve: ""
      });
      setSelectedClient(null);
      setCapturedPhoto(null);
    } catch (error) {
      toast.error("Gabim në krijimin e tiketit");
      console.error("Create ticket error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    if (client) {
      setFormData(prev => ({
        ...prev,
        client_emri: client.emri,
        client_telefoni: client.telefoni,
        client_email: client.email || ""
      }));
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAccessoryChange = (accessoryName, checked) => {
    setFormData(prev => ({
      ...prev,
      paisjet_percjellse: checked
        ? [...prev.paisjet_percjellse, accessoryName]
        : prev.paisjet_percjellse.filter(a => a !== accessoryName)
    }));
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center">
          <Plus className="w-5 h-5 mr-2" />
          Regjistro Tiket të Re
        </CardTitle>
        <CardDescription>
          Plotëso të dhënat për të krijuar një ticket servisimi të re. Vetëm emri dhe telefoni janë të detyrueshëm.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Client Search */}
          <ClientSearch
            selectedClient={selectedClient}
            onSelectClient={handleClientSelect}
          />

          {/* Client Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <User className="w-5 h-5 mr-2" />
              Të Dhënat e Klientit
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="client_emri">Emri i Klientit *</Label>
                <Input
                  id="client_emri"
                  value={formData.client_emri}
                  onChange={(e) => handleChange("client_emri", e.target.value)}
                  required
                  placeholder="Emri dhe mbiemri"
                />
              </div>
              <div>
                <Label htmlFor="client_telefoni">Telefoni *</Label>
                <Input
                  id="client_telefoni"
                  value={formData.client_telefoni}
                  onChange={(e) => handleChange("client_telefoni", e.target.value)}
                  required
                  placeholder="+355 69 123 4567"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="client_email">Email</Label>
                <Input
                  id="client_email"
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => handleChange("client_email", e.target.value)}
                  placeholder="klient@email.com"
                />
              </div>
            </div>
          </div>

          {/* Device Information */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Monitor className="w-5 h-5 mr-2" />
              Të Dhënat e Paisjes (opsionale)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Lloji i Paisjes</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {deviceTypes.map((device) => (
                    <div key={device.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={device.key}
                        checked={formData.paisja === device.key}
                        onCheckedChange={(checked) => 
                          handleChange("paisja", checked ? device.key : "")
                        }
                      />
                      <Label htmlFor={device.key} className="text-sm">
                        {device.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <Label>Marka</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-40 overflow-y-auto">
                  {brands.map((brand) => (
                    <div key={brand.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={brand.name}
                        checked={formData.marka === brand.name}
                        onCheckedChange={(checked) => 
                          handleChange("marka", checked ? brand.name : "")
                        }
                      />
                      <Label htmlFor={brand.name} className="text-sm">
                        {brand.name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <Label htmlFor="modeli">Modeli</Label>
                <Input
                  id="modeli"
                  value={formData.modeli}
                  onChange={(e) => handleChange("modeli", e.target.value)}
                  placeholder="Model i paisjes"
                />
              </div>
              <div>
                <Label htmlFor="serial_number">Numri Serial</Label>
                <Input
                  id="serial_number"
                  value={formData.serial_number}
                  onChange={(e) => handleChange("serial_number", e.target.value)}
                  placeholder="Serial number"
                />
              </div>
            </div>
          </div>

          {/* Problem Description */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Përshkrimi i Problemit</h3>
            <Textarea
              id="pershkrimi_problemit"
              value={formData.pershkrimi_problemit}
              onChange={(e) => handleChange("pershkrimi_problemit", e.target.value)}
              placeholder="Përshkruani problemin që ka paisja"
              rows={4}
            />
          </div>

          {/* Installation/Formatting Section */}
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2" />
              Instalimi / Formatizimi
            </h3>
            <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
              {/* Formatimi Checkbox */}
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="formatimi"
                  checked={formData.formatimi}
                  onCheckedChange={(checked) => handleChange("formatimi", checked)}
                />
                <Label htmlFor="formatimi" className="text-sm font-medium cursor-pointer">
                  Formatizimi i sistemit
                </Label>
              </div>

              {/* Ruaj Shënimet Checkbox */}
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="ruaj_shenimet"
                  checked={formData.ruaj_shenimet}
                  onCheckedChange={(checked) => handleChange("ruaj_shenimet", checked)}
                />
                <Label htmlFor="ruaj_shenimet" className="text-sm font-medium cursor-pointer">
                  Ruaj shënimet / të dhënat
                </Label>
              </div>

              {/* Programe Shtese Checkbox */}
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="programe_shtese"
                  checked={formData.programe_shtese}
                  onCheckedChange={(checked) => handleChange("programe_shtese", checked)}
                />
                <Label htmlFor="programe_shtese" className="text-sm font-medium cursor-pointer">
                  Instalim i programeve shtesë
                </Label>
              </div>

              {/* Lista e Programeve - Shows only if programe_shtese is checked */}
              {formData.programe_shtese && (
                <div className="mt-3 pl-7">
                  <Label htmlFor="lista_programeve" className="text-sm text-slate-600 mb-2 block">
                    Lista e programeve për instalim:
                  </Label>
                  <Textarea
                    id="lista_programeve"
                    value={formData.lista_programeve}
                    onChange={(e) => handleChange("lista_programeve", e.target.value)}
                    placeholder="p.sh: Microsoft Office, Chrome, Adobe Reader, Zoom, etj."
                    rows={3}
                    className="text-sm"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Paisjet Percjellëse */}
          <div>
            <h3 className="text-lg font-semibold mb-4">Paisjet Percjellëse</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-40 overflow-y-auto">
              {accessories.map((accessory) => (
                <div key={accessory.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={accessory.name}
                    checked={formData.paisjet_percjellse.includes(accessory.name)}
                    onCheckedChange={(checked) => 
                      handleAccessoryChange(accessory.name, checked)
                    }
                  />
                  <Label htmlFor={accessory.name} className="text-sm">
                    {accessory.name}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Photo Capture */}
          <div>
            <PhotoCapture 
              onPhotoCapture={setCapturedPhoto}
              capturedPhoto={capturedPhoto}
            />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Duke regjistruar..." : "Regjistro Tiketin"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

// Status Update Modal Component
const StatusUpdateModal = ({ ticket, isOpen, onClose, onUpdate, statuses }) => {
  const [selectedStatus, setSelectedStatus] = useState(ticket?.statusi || "");
  const [note, setNote] = useState("");
  const [price, setPrice] = useState(ticket?.cmimi || "");
  const [paid, setPaid] = useState(ticket?.paguar || false);
  const [pjesePercjellse, setPjesePercjellse] = useState(ticket?.pjese_percjellse || "");
  const [riparimeShtes, setRiparimeShtes] = useState(ticket?.riparime_shtese || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ticket) {
      setSelectedStatus(ticket.statusi);
      setNote("");
      setPrice(ticket.cmimi || "");
      setPaid(ticket.paguar || false);
      setPjesePercjellse(ticket.pjese_percjellse || "");
      setRiparimeShtes(ticket.riparime_shtese || "");
    }
  }, [ticket]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const updateData = {
        statusi: selectedStatus,
        shenim_teknik: note || null,
        cmimi: parseFloat(price) || null,
        paguar: paid
      };

      // Add extra fields for "gati" status
      if (selectedStatus === "gati") {
        updateData.pjese_percjellse = pjesePercjellse || null;
        updateData.riparime_shtese = riparimeShtes || null;
      }

      await onUpdate(ticket.id, updateData);
      onClose();
    } catch (error) {
      console.error("Update error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !ticket) return null;

  const isCompleted = selectedStatus === "gati";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-lg font-semibold mb-4">
          Përditëso Statusin - {ticket.ticket_number}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="status">Statusi i Ri</Label>
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.key} value={status.key}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="note">Shënim Teknik</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Shto shënim për këtë përditësim..."
              rows={3}
            />
          </div>

          {isCompleted && (
            <>
              <div>
                <Label htmlFor="price">Çmimi (€) *</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="pjese_percjellse">Pjesë Percjellëse</Label>
                <Textarea
                  id="pjese_percjellse"
                  value={pjesePercjellse}
                  onChange={(e) => setPjesePercjellse(e.target.value)}
                  placeholder="P.sh. Bateri e re, RAM 8GB, Ekran i ri..."
                  rows={2}
                />
                <p className="text-xs text-slate-500 mt-1">Shëno pjesët që janë vendosur gjatë riparimit</p>
              </div>
              
              <div>
                <Label htmlFor="riparime_shtese">Riparime Shtesë</Label>
                <Textarea
                  id="riparime_shtese"
                  value={riparimeShtes}
                  onChange={(e) => setRiparimeShtes(e.target.value)}
                  placeholder="P.sh. Pastrimi i fansave, Përditësimi i sistemit, Instalimi i software..."
                  rows={2}
                />
                <p className="text-xs text-slate-500 mt-1">Shëno punë shtesë që janë bërë</p>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="paid"
                  checked={paid}
                  onCheckedChange={setPaid}
                />
                <Label htmlFor="paid">Paguar</Label>
              </div>
            </>
          )}

          <div className="flex space-x-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Anulo
            </Button>
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? "Duke përditësuar..." : "Përditëso"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// All Tickets Component with Auto-refresh
const AllTickets = () => {
  const { user } = useAuth(); // Get user from AuthContext
  const [tickets, setTickets] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusMap, setStatusMap] = useState(defaultStatusMap);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // New states for ticket details modal (same as Dashboard)
  const [showTicketDetail, setShowTicketDetail] = useState(false);
  const [loadingTicketDetail, setLoadingTicketDetail] = useState(false);

  useEffect(() => {
    fetchTickets();
    fetchStatuses();
  }, [selectedStatus]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchTickets();
    }, 15000); // Refresh every 15 seconds for tickets

    return () => clearInterval(interval);
  }, [autoRefresh, selectedStatus]);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/config/statuses`);
      setStatuses(response.data);
      
      // Create dynamic status map
      const dynamicStatusMap = {};
      response.data.forEach(status => {
        const iconMap = {
          Clock: Clock,
          Wrench: Wrench,
          TestTube: TestTube,
          CheckCircle: CheckCircle
        };
        
        dynamicStatusMap[status.key] = {
          label: status.label,
          color: status.color,
          icon: iconMap[status.icon] || Clock
        };
      });
      setStatusMap(dynamicStatusMap);
    } catch (error) {
      console.error("Fetch statuses error:", error);
    }
  };

  const fetchTickets = async () => {
    try {
      const url = selectedStatus === "all" ? `${API}/tickets` : `${API}/tickets?statusi=${selectedStatus}`;
      const response = await axios.get(url);
      setTickets(response.data);
    } catch (error) {
      toast.error("Gabim në ngarkimin e ticketave");
      console.error("Fetch tickets error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (ticketId, updateData) => {
    try {
      await axios.put(`${API}/tickets/${ticketId}/status`, updateData);
      toast.success("Statusi u përditësua me sukses");
      fetchTickets(); // Refresh the list
    } catch (error) {
      toast.error("Gabim në përditësimin e statusit");
      console.error("Update status error:", error);
    }
  };

  // ESC key listener for modals
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showTicketDetail) {
          setShowTicketDetail(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showTicketDetail]);

  const handleShowTicketDetails = async (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketDetail(true);
  };

  const handleSendSMS = async (ticketId, phoneNumber, ticketNumber) => {
    try {
      const response = await axios.post(`${API}/tickets/${ticketId}/send-sms`, {
        message: `Përshëndetje! Tiketi juaj ${ticketNumber} është gati për marrje. IT Solution`
      });
      
      toast.success('SMS u dërgua me sukses');
    } catch (error) {
      toast.error('Gabim në dërgimin e SMS');
      console.error('Send SMS error:', error);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      const response = await axios.put(`${API}/tickets/${ticketId}/status`, {
        statusi: newStatus
      });
      
      toast.success('Statusi u ndryshua me sukses');
      
      // Update the selected ticket in state
      setSelectedTicket(prev => ({ ...prev, statusi: newStatus }));
      
      // Refresh tickets list
      fetchTickets();
    } catch (error) {
      toast.error('Gabim në ndryshimin e statusit');
      console.error('Status change error:', error);
    }
  };

  const handlePrintTicket = (ticket) => {
    const printWindow = window.open('', '', 'width=300,height=400');
    const printContent = `
      <html>
        <head>
          <title>Print Ticket</title>
          <style>
            body { font-family: 'Courier New', monospace; font-size: 12px; margin: 0; padding: 10px; }
            .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
            .ticket-info { margin-bottom: 10px; }
            .row { display: flex; justify-content: space-between; margin-bottom: 5px; }
            .label { font-weight: bold; }
            .separator { border-top: 1px dashed #000; margin: 10px 0; }
            .footer { text-align: center; margin-top: 10px; font-size: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>IT SOLUTION</h2>
            <p>Servisim & Mirëmbajtje</p>
          </div>
          
          <div class="ticket-info">
            <div class="row">
              <span class="label">Ticket:</span>
              <span>${ticket.ticket_number}</span>
            </div>
            <div class="row">
              <span class="label">Klienti:</span>
              <span>${ticket.client_emri}</span>
            </div>
            <div class="row">
              <span class="label">Telefoni:</span>
              <span>${ticket.client_telefoni}</span>
            </div>
            ${ticket.paisja ? `
            <div class="row">
              <span class="label">Paisja:</span>
              <span>${ticket.paisja} ${ticket.marka || ''}</span>
            </div>
            ` : ''}
            ${ticket.modeli ? `
            <div class="row">
              <span class="label">Modeli:</span>
              <span>${ticket.modeli}</span>
            </div>
            ` : ''}
            ${ticket.serial_number ? `
            <div class="row">
              <span class="label">Serial:</span>
              <span>${ticket.serial_number}</span>
            </div>
            ` : ''}
            <div class="row">
              <span class="label">Data:</span>
              <span>${new Date(ticket.data_regjistrimit).toLocaleDateString('sq-AL')}</span>
            </div>
          </div>
          
          <div class="separator"></div>
          
          <div class="ticket-info">
            ${ticket.pershkrimi_problemit ? `
            <div class="label">Problemi:</div>
            <p>${ticket.pershkrimi_problemit}</p>
            ` : ''}
            
            ${ticket.paisjet_percjellse && ticket.paisjet_percjellse.length > 0 ? `
            <div class="label">Paisje Percjellëse:</div>
            <p>${ticket.paisjet_percjellse.join(', ')}</p>
            ` : ''}
            
            ${ticket.cmimi ? `
            <div class="row">
              <span class="label">Çmimi:</span>
              <span>€${ticket.cmimi}</span>
            </div>
            ` : ''}
          </div>
          
          <div class="separator"></div>
          
          <div class="footer">
            <p>Ruajeni këtë faturë për marrjen e paisjes</p>
            <p>Tel: +355 xx xxx xxxx</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
    printWindow.close();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Duke ngarkuar ticketat...</div>
      </div>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Menaxhimi i Tiketave</CardTitle>
              <CardDescription>Lista e të gjitha tiketave të servisimit</CardDescription>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="tickets-auto-refresh" 
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                />
                <Label htmlFor="tickets-auto-refresh" className="text-sm">
                  Auto-refresh (15s)
                </Label>
                {autoRefresh && (
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <Filter className="w-4 h-4 text-slate-500" />
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të Gjitha</SelectItem>
                    {statuses.map((status) => (
                      <SelectItem key={status.key} value={status.key}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {tickets.length === 0 ? (
            <p className="text-slate-500 text-center py-8">Nuk ka ticket për tu shfaqur</p>
          ) : (
            <div className="space-y-4">
              {tickets.map((ticket) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  statusMap={statusMap}
                  onStatusUpdate={(ticketId, newStatus) => {
                    setSelectedTicket(ticket);
                    setIsModalOpen(true);
                  }}
                  onPrint={handlePrintTicket}
                  onShowDetails={handleShowTicketDetails}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <StatusUpdateModal
        ticket={selectedTicket}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onUpdate={handleStatusUpdate}
        statuses={statuses}
      />
      
      {/* Individual Ticket Detail Modal - Same as Dashboard */}
      {showTicketDetail && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowTicketDetail(false);
            }
          }}
        >
          <Card className="w-full max-w-3xl max-h-[80vh] overflow-hidden">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center space-x-2">
                  <Badge variant="outline" className="font-mono">
                    {selectedTicket?.ticket_number}
                  </Badge>
                  <span>Detajet e Tiketit</span>
                </CardTitle>
                <div className="flex items-center space-x-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleSendSMS(selectedTicket?.id, selectedTicket?.client_telefoni, selectedTicket?.ticket_number)}
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Dërgo SMS
                  </Button>
                  <Button variant="ghost" onClick={() => setShowTicketDetail(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="overflow-y-auto max-h-[60vh]">
              {loadingTicketDetail ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Duke ngarkuar detajet...</p>
                </div>
              ) : selectedTicket ? (
                <div className="space-y-4">
                  {/* Client Information */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Informacionet e Klientit</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <span className="text-sm font-medium text-slate-600">Emri:</span>
                        <p className="font-semibold">{selectedTicket.client_emri}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-600">Telefoni:</span>
                        <div className="mt-1">
                          <MessagingButtons 
                            phoneNumber={selectedTicket.client_telefoni}
                            clientName={selectedTicket.client_emri}
                            ticketNumber={selectedTicket.ticket_number}
                          />
                        </div>
                      </div>
                      {selectedTicket.client_email && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Email:</span>
                          <p className="font-semibold">{selectedTicket.client_email}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Device Information */}
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Informacionet e Paisjes</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedTicket.paisja && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Paisja:</span>
                          <p className="font-semibold">{selectedTicket.paisja}</p>
                        </div>
                      )}
                      {selectedTicket.marka && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Marka:</span>
                          <p className="font-semibold">{selectedTicket.marka}</p>
                        </div>
                      )}
                      {selectedTicket.modeli && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Modeli:</span>
                          <p className="font-semibold">{selectedTicket.modeli}</p>
                        </div>
                      )}
                      {selectedTicket.numri_serik && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Serial Number:</span>
                          <p className="font-semibold font-mono text-sm">{selectedTicket.numri_serik}</p>
                        </div>
                      )}
                    </div>
                    
                    {selectedTicket.paisjet_percjellse && selectedTicket.paisjet_percjellse.length > 0 && (
                      <div className="mt-3">
                        <span className="text-sm font-medium text-slate-600">Paisje të përfshira:</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedTicket.paisjet_percjellse.map((item, index) => (
                            <Badge key={index} variant="secondary">{item}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Problem Description */}
                  {selectedTicket.pershkrimi_problemit && (
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-2">Përshkrimi i Problemit</h3>
                      <p className="text-slate-700">{selectedTicket.pershkrimi_problemit}</p>
                    </div>
                  )}

                  {/* Installation/Formatting Section - Read Only */}
                  {(selectedTicket.formatimi || selectedTicket.ruaj_shenimet || selectedTicket.programe_shtese || selectedTicket.lista_programeve) && (
                    <div className="bg-indigo-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center">
                        <Settings className="w-5 h-5 mr-2" />
                        Instalimi / Formatizimi
                      </h3>
                      <div className="space-y-2">
                        {selectedTicket.formatimi && (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium">Formatizimi i sistemit</span>
                          </div>
                        )}
                        {selectedTicket.ruaj_shenimet && (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium">Ruaj shënimet / të dhënat</span>
                          </div>
                        )}
                        {selectedTicket.programe_shtese && (
                          <div className="flex items-center space-x-2">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium">Instalim i programeve shtesë</span>
                          </div>
                        )}
                        {selectedTicket.lista_programeve && (
                          <div className="mt-2 pl-6">
                            <span className="text-xs font-medium text-slate-600 block mb-1">Programet për instalim:</span>
                            <p className="text-sm text-slate-700 bg-white p-2 rounded border border-slate-200">
                              {selectedTicket.lista_programeve}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Device Photo */}
                  {selectedTicket.device_photo_url && (
                    <div className="bg-slate-100 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-2 flex items-center">
                        <Camera className="w-5 h-5 mr-2" />
                        Fotografia e Paisjes
                      </h3>
                      <div className="mt-2">
                        <img 
                          src={`${API.replace('/api', '')}${selectedTicket.device_photo_url}`}
                          alt="Device" 
                          className="max-w-full h-auto rounded cursor-pointer hover:shadow-lg transition border-2 border-slate-300"
                          onClick={() => {
                            const modal = document.createElement('div');
                            modal.className = 'fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50';
                            modal.onclick = () => modal.remove();
                            modal.innerHTML = `
                              <div class="relative max-w-4xl max-h-screen p-4">
                                <img src="${API.replace('/api', '')}${selectedTicket.device_photo_url}" 
                                     class="max-w-full max-h-screen rounded-lg" 
                                     alt="Device Photo" />
                                <button class="absolute top-6 right-6 text-white bg-red-500 rounded-full w-10 h-10 flex items-center justify-center text-2xl hover:bg-red-600">×</button>
                              </div>
                            `;
                            document.body.appendChild(modal);
                          }}
                          style={{ maxHeight: '150px' }}
                        />
                        <p className="text-xs text-slate-500 mt-1">📸 Kliko për ta zmadhuar</p>
                      </div>
                    </div>
                  )}

                  {/* Status and Dates */}
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Statusi dhe Datat</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <span className="text-sm font-medium text-slate-600">Statusi Aktual:</span>
                        <div className="mt-1 flex items-center space-x-2">
                          <Badge className={`${statusMap[selectedTicket.statusi]?.color} text-white`}>
                            {statusMap[selectedTicket.statusi]?.label}
                          </Badge>
                          <Select 
                            value={selectedTicket.statusi} 
                            onValueChange={(value) => handleStatusChange(selectedTicket.id, value)}
                          >
                            <SelectTrigger className="w-40">
                              <SelectValue placeholder="Ndrysho statusin" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(statusMap).map(([key, status]) => (
                                <SelectItem key={key} value={key}>
                                  <div className="flex items-center space-x-2">
                                    <div className={`w-3 h-3 rounded-full ${status.color}`}></div>
                                    <span>{status.label}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <span className="text-sm font-medium text-slate-600">Data e Regjistrimit:</span>
                        <p className="font-semibold">
                          {new Date(selectedTicket.data_regjistrimit).toLocaleDateString('sq-AL')} në {new Date(selectedTicket.data_regjistrimit).toLocaleTimeString('sq-AL')}
                        </p>
                      </div>
                      {selectedTicket.data_statusit && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Status ndryshua:</span>
                          <p className="font-semibold">
                            {new Date(selectedTicket.data_statusit).toLocaleDateString('sq-AL')} në {new Date(selectedTicket.data_statusit).toLocaleTimeString('sq-AL')}
                          </p>
                        </div>
                      )}
                      {selectedTicket.cmimi && (
                        <div>
                          <span className="text-sm font-medium text-slate-600">Çmimi:</span>
                          <div className="flex items-center space-x-2">
                            <p className="font-semibold">€{selectedTicket.cmimi}</p>
                            {selectedTicket.paguar && (
                              <Badge variant="outline" className="text-green-600 border-green-600">
                                Paguar
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                      {selectedTicket.statusi === 'gati' && (
                        <>
                          {selectedTicket.pjese_percjellse && (
                            <div>
                              <span className="text-sm font-medium text-slate-600">Pjesë Percjellëse:</span>
                              <p className="font-semibold">{selectedTicket.pjese_percjellse}</p>
                            </div>
                          )}
                          {selectedTicket.riparime_shtese && (
                            <div>
                              <span className="text-sm font-medium text-slate-600">Riparime Shtesë:</span>
                              <p className="font-semibold">{selectedTicket.riparime_shtese}</p>
                            </div>
                          )}
                          {selectedTicket.cmimi && !selectedTicket.paguar && (
                            <div className="bg-yellow-100 border border-yellow-300 rounded p-3">
                              <p className="text-sm font-medium text-yellow-800">
                                ⚠️ Pagesa e papërfunduar: €{selectedTicket.cmimi}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                      {selectedTicket.statusi === 'gati' && (
                        <>
                          {selectedTicket.pjese_percjellse && (
                            <div>
                              <span className="text-sm font-medium text-slate-600">Pjesë Percjellëse:</span>
                              <p className="font-semibold">{selectedTicket.pjese_percjellse}</p>
                            </div>
                          )}
                          {selectedTicket.riparime_shtese && (
                            <div>
                              <span className="text-sm font-medium text-slate-600">Riparime Shtesë:</span>
                              <p className="font-semibold">{selectedTicket.riparime_shtese}</p>
                            </div>
                          )}
                          {selectedTicket.cmimi && !selectedTicket.paguar && (
                            <div className="bg-yellow-100 border border-yellow-300 rounded p-3">
                              <p className="text-sm font-medium text-yellow-800">
                                ⚠️ Pagesa e papërfunduar: €{selectedTicket.cmimi}
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Technical Notes */}
                  {selectedTicket.shenim_teknik && (
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-2">Shënimet Teknike</h3>
                      <p className="text-slate-700">{selectedTicket.shenim_teknik}</p>
                    </div>
                  )}

                  {/* Payment Information - Always show for receipts */}
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Informacioni i Pagesës</h3>
                    {selectedTicket.cmimi || selectedTicket.paguar ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          {selectedTicket.cmimi && (
                            <div>
                              <span className="text-sm font-medium text-slate-600">Çmimi:</span>
                              <p className="font-semibold text-green-600 text-lg">
                                €{selectedTicket.cmimi}
                              </p>
                            </div>
                          )}
                          <div>
                            <span className="text-sm font-medium text-slate-600">Statusi i Pagesës:</span>
                            <div className="mt-1">
                              <Badge variant={
                                !selectedTicket.cmimi || selectedTicket.cmimi === 0 
                                  ? "secondary" 
                                  : selectedTicket.paguar 
                                    ? "default" 
                                    : "destructive"
                              }>
                                {!selectedTicket.cmimi || selectedTicket.cmimi === 0 
                                  ? "Nuk ka Pagese" 
                                  : selectedTicket.paguar 
                                    ? "I paguar" 
                                    : "I papaguar"}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-sm text-slate-600 mb-3">Çmimi nuk është vendosur ende</p>
                    )}
                    
                    {/* Print Receipt Section */}
                    <div className="pt-3 border-t border-purple-200">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-600">Printim:</span>
                        <span className="text-xs text-slate-500">Disponueshëm në Dashboard</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-center py-8 text-slate-500">Nuk u gjetën detaje për këtë tiket</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

// Ticket Card Component
const TicketCard = ({ ticket, statusMap, onStatusUpdate, onPrint, onShowDetails }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const currentStatus = statusMap[ticket.statusi];
  const StatusIcon = currentStatus?.icon || Clock;

  return (
    <div className="border rounded-lg hover:shadow-md transition-shadow">
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Badge 
              variant="outline" 
              className="font-mono cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => onShowDetails(ticket)}
              title="Kliko për të parë detajet"
            >
              {ticket.ticket_number}
            </Badge>
            <div>
              <h3 className="font-semibold text-slate-900">{ticket.client_emri}</h3>
              <p className="text-sm text-slate-600">
                {ticket.paisja && `${ticket.paisja} ${ticket.marka || ''} ${ticket.modeli || ''}`.trim()}
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <Badge className={`${currentStatus?.color} text-white flex items-center`}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {currentStatus?.label}
            </Badge>
            {ticket.cmimi && (
              <Badge variant="outline" className="flex items-center">
                <Euro className="w-3 h-3 mr-1" />
                {ticket.cmimi}
                {ticket.paguar && <span className="ml-1 text-green-600">✓</span>}
              </Badge>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPrint(ticket)}
            >
              <Printer className="w-3 h-3 mr-1" />
              Print
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onShowDetails(ticket)}
            >
              Detaje
            </Button>
          </div>
        </div>

        {isExpanded && (
          <div className="mt-4 pt-4 border-t space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p><strong>Telefoni:</strong> {ticket.client_telefoni}</p>
                {ticket.client_email && <p><strong>Email:</strong> {ticket.client_email}</p>}
                {ticket.serial_number && <p><strong>Serial:</strong> {ticket.serial_number}</p>}
              </div>
              <div>
                <p><strong>Data e Regjistrimit:</strong> {new Date(ticket.data_regjistrimit).toLocaleDateString('sq-AL')}</p>
                {ticket.paisjet_percjellse && ticket.paisjet_percjellse.length > 0 && (
                  <p><strong>Paisje Percjellëse:</strong> {ticket.paisjet_percjellse.join(', ')}</p>
                )}
                {ticket.data_perfundimit && (
                  <p><strong>Data e Perfundimit:</strong> {new Date(ticket.data_perfundimit).toLocaleDateString('sq-AL')}</p>
                )}
                {ticket.data_statusit && (
                  <p><strong>Status ndryshua:</strong> {new Date(ticket.data_statusit).toLocaleDateString('sq-AL')} në {new Date(ticket.data_statusit).toLocaleTimeString('sq-AL')}</p>
                )}
              </div>
            </div>
            
            {ticket.pershkrimi_problemit && (
              <div>
                <p className="font-medium mb-2">Përshkrimi i Problemit:</p>
                <p className="text-slate-600 bg-slate-50 p-3 rounded">{ticket.pershkrimi_problemit}</p>
              </div>
            )}

            {ticket.shenim_teknik && (
              <div>
                <p className="font-medium mb-2">Shënim Teknik:</p>
                <p className="text-slate-600 bg-blue-50 p-3 rounded">{ticket.shenim_teknik}</p>
              </div>
            )}

            <div className="flex justify-between items-center">
              <Button
                onClick={() => onStatusUpdate(ticket.id)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Përditëso Statusin
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Clients Page Component with Search, Edit, Delete, and Auto-refresh
const ClientsPage = () => {
  const { user } = useAuth(); // Get user from AuthContext
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchClients();
  }, [searchTerm]);

  // Auto-refresh functionality
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchClients();
    }, 10000); // Refresh every 10 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, searchTerm]);

  const fetchClients = async () => {
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : '';
      const response = await axios.get(`${API}/clients${params}`);
      setClients(response.data);
    } catch (error) {
      toast.error("Gabim në ngarkimin e klientëve");
      console.error("Fetch clients error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setIsEditModalOpen(true);
  };

  const handleDeleteClient = async (client) => {
    const confirmed = window.confirm(
      `Jeni i sigurt që dëshironi të fshini klientin "${client.emri}"?\n\n⚠️ VËREJTJE: Nuk mund të fshihet nëse ka tiketa aktive!\nVetëm klientët me tiketa të përfunduara mund të fshihen.`
    );
    
    if (!confirmed) return;

    try {
      const response = await axios.delete(`${API}/clients/${client.id}`);
      toast.success(response.data.message || "Klienti u fshi me sukses");
      fetchClients(); // Refresh the list
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Gabim në fshirjen e klientit";
      toast.error(errorMessage);
      console.error("Delete client error:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate-600">Duke ngarkuar klientët...</div>
      </div>
    );    
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle>Lista e Klientëve</CardTitle>
              <CardDescription>Kërko dhe shiko raportet e detajuara për çdo klient</CardDescription>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="auto-refresh" 
                  checked={autoRefresh}
                  onCheckedChange={setAutoRefresh}
                />
                <Label htmlFor="auto-refresh" className="text-sm">
                  Auto-refresh (10s)
                </Label>
              </div>
              {autoRefresh && (
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              )}
            </div>
          </div>
          
          {/* Search */}
          <div className="flex items-center space-x-2 mt-4">
            <div className="relative flex-1">
              <Input
                placeholder="Kërko sipas emrit, telefonit ose email-it..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10"
              />
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {clients.length === 0 ? (
            <p className="text-slate-500 text-center py-8">
              {searchTerm ? `Nuk u gjend klient për "${searchTerm}"` : "Nuk ka klientë të regjistruar"}
            </p>
          ) : (
            <div className="space-y-4">
              {clients.map((client) => (
                <div key={client.id} className="border rounded-lg p-4 hover:bg-slate-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-slate-900">{client.emri}</h3>
                      <p className="text-sm text-slate-600">{client.telefoni}</p>
                      {client.email && <p className="text-sm text-slate-500">{client.email}</p>}
                      <p className="text-xs text-slate-400">
                        Regjistruar: {new Date(client.created_at).toLocaleDateString('sq-AL')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      {user?.role === "admin" && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => handleEditClient(client)}>
                            <Edit3 className="w-4 h-4 mr-1" />
                            Ndrysho
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm" 
                            onClick={() => handleDeleteClient(client)}
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Fshi
                          </Button>
                        </>
                      )}
                      <Link to={`/clients/${client.id}/report`}>
                        <Button variant="outline" size="sm">
                          <FileText className="w-4 h-4 mr-1" />
                          Raport
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ClientEditModal
        client={selectedClient}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedClient(null);
        }}
        onUpdate={fetchClients}
      />
    </>
  );
};

// Mobile Status Check Component
const MobileStatusCheck = () => {
  const [ticketNumber, setTicketNumber] = useState("");
  const [ticketStatus, setTicketStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const checkStatus = async () => {
    if (!ticketNumber.trim()) {
      setError("Ju lutem shkruani numrin e ticketit");
      return;
    }

    setLoading(true);
    setError("");
    setTicketStatus(null);

    try {
      const response = await axios.get(`${API}/ticket-status/${ticketNumber.trim()}`);
      setTicketStatus(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setError("Tiketi nuk u gjet. Kontrolloni numrin e tiketës.");
      } else {
        setError("Gabim në kontrollin e statusit. Provoni përsëri.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    checkStatus();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mb-4">
            <Eye className="w-8 h-8 text-white" />
          </div>
          <img src={LOGO_URL} alt="IT Solution" className="h-12 w-auto mx-auto mb-4" />
          <h2 className="text-2xl text-center text-slate-700">Kontrollo Statusin</h2>
          <CardDescription>
            Shkruani numrin e tiketës për të kontrolluar statusin e paisjes suaj
          </CardDescription>
          
          {/* Company Location */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">IT Solution</p>
                <p className="text-xs text-blue-600">Qendra e Riparimit</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open('https://maps.app.goo.gl/qhVFP388TGYk8rJT7', '_blank')}
                className="bg-white border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                </svg>
                Harta
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="ticket_number">Numri i Tiketës</Label>
              <Input
                id="ticket_number"
                placeholder="p.sh. IT1209251"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value.toUpperCase())}
                className="text-center font-mono text-lg"
              />
            </div>
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Duke kontrolluar..." : "Kontrollo Statusin"}
            </Button>
          </form>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {error}
            </div>
          )}

          {ticketStatus && (
            <div className="space-y-4 p-4 bg-white border rounded-lg shadow-sm">
              <div className="text-center">
                <Badge className="text-lg px-4 py-2 bg-blue-500 text-white">
                  {ticketStatus.statusi}
                </Badge>
              </div>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-600">Klienti:</span>
                  <span className="font-semibold text-slate-900">{ticketStatus.client_emri}</span>
                </div>
                {ticketStatus.paisja && (
                  <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                    <span className="font-medium text-slate-600">Paisja:</span>
                    <span className="font-semibold text-slate-900">
                      {ticketStatus.paisja} {ticketStatus.marka || ''} {ticketStatus.modeli || ''}
                    </span>
                  </div>
                )}
              </div>

              {ticketStatus.statusi === "Gati" && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-800 text-center">
                  <CheckCircle className="w-5 h-5 mx-auto mb-2" />
                  <p className="font-medium">Paisja është gati për marrje!</p>
                  <p className="text-sm">Mund të vini ta merrni gjatë orëve të punës.</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Chat Popup */}
      <ChatPopup ticketNumber={ticketStatus?.ticket_number} />
    </div>
  );
};

// App Content Component (contains the routes)
const AppContent = () => {
  const { user, loading, logout } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Login />; // Login component handles login internally via AuthContext
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BrowserRouter>
        <Routes>
          {/* Public Status Check Route - NO AUTH REQUIRED */}
          <Route path="/status" element={<PublicStatusCheck />} />
          
          <Route path="/" element={
            <>
              <Navigation user={user} onLogout={logout} />
              <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <Dashboard />
              </main>
            </>
          } />
          <Route path="/new-ticket" element={
            <>
              <Navigation user={user} onLogout={logout} />
              <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <NewTicketForm />
              </main>
            </>
          } />
          <Route path="/tickets" element={
            <>
              <Navigation user={user} onLogout={logout} />
              <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <AllTickets />
              </main>
            </>
          } />
          <Route path="/clients" element={
            <>
              <Navigation user={user} onLogout={logout} />
              <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <ClientsPage />
              </main>
            </>
          } />
          <Route path="/clients/:clientId/report" element={
            <>
              <Navigation user={user} onLogout={logout} />
              <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <ClientReport />
              </main>
            </>
          } />
          {user.role === 'admin' && (
            <>
              <Route path="/admin" element={
                <>
                  <Navigation user={user} onLogout={logout} />
                  <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <AdminPanel />
                  </main>
                </>
              } />
              <Route path="/admin/backup" element={
                <>
                  <Navigation user={user} onLogout={logout} />
                  <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <AdminBackupExport />
                  </main>
                </>
              } />
              <Route path="/admin/clients-export" element={
                <>
                  <Navigation user={user} onLogout={logout} />
                  <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <AdminBackupExport />
                  </main>
                </>
              } />
              <Route path="/admin/finances" element={
                <>
                  <Navigation user={user} onLogout={logout} />
                  <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <FinancialManagement />
                  </main>
                </>
              } />
              <Route path="/admin/status-manager" element={
                <>
                  <Navigation user={user} onLogout={logout} />
                  <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <StatusManager />
                  </main>
                </>
              } />
              <Route path="/admin/sms-config" element={
                <>
                  <Navigation user={user} onLogout={logout} />
                  <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <SMSConfiguration />
                  </main>
                </>
              } />
              <Route path="/admin/messaging-config" element={
                <>
                  <Navigation user={user} onLogout={logout} />
                  <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <MessagingConfiguration />
                  </main>
                </>
              } />
              
              <Route path="/orders" element={
                <>
                  <Navigation user={user} onLogout={logout} />
                  <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <OrdersManager />
                  </main>
                </>
              } />
              <Route path="/users" element={
                <>
                  <Navigation user={user} onLogout={logout} />
                  <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                    <UserManagement />
                  </main>
                </>
              } />
            </>
          )}
          <Route path="/2fa-settings" element={
            <>
              <Navigation user={user} onLogout={logout} />
              <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <TwoFactorManagement user={user} />
              </main>
            </>
          } />
          <Route path="/status" element={<MobileStatusCheck />} />
          <Route path="/status/:ticketNumber" element={<MobileStatusCheck />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
};

// Main App Component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;